import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from "react-router-dom";
import {
    Plus, Mail,
    ChevronUp, ChevronDown, ChevronRight, X, Send,
    FileX, Loader2, Check, Filter,
    User, ExternalLink, Eye, MoreVertical,
    FileText, Upload, UserCheck, Copy, Clock, MessageSquare, Link as LinkIcon,
    UserPlus, Folder, Info, Sparkles
} from 'lucide-react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import api, { expireInvite } from '../../../../api/client';
import NotificationPopup from '../../component/NotificationPopup';
import ViewResult from '../../component/ViewResult';
import GenerateInviteSuccessfully from '../../component/GenerateInviteSuccessfully';
import { useNavigate } from 'react-router-dom';
import { useCandidates, useJobDescriptions, useSkillAnalysis } from '../../../../hooks/useCandidateDetailApis';
import useViewportPageSize from '../../../../hooks/useViewportPageSize';

import GenerateInviteModal from '../../../modals/GenerateInviteModal';
import SkillAnalysisModal from './SkillAnalysisModal';
import MultiJDScheduleModal from './MultiJDScheduleModal';
import { IoArchiveOutline } from 'react-icons/io5';
import Pagination from '../../component/Pagination';
import Timeline from '../../component/Timeline';

import BulkResumeUpload from './BulkResumeUpload.tsx';
import CandidateGroupSelector from '../candidateGroups/CandidateGroupSelector';
import { createCandidateGroup, addCandidatesToGroup } from '../candidateGroups/candidateGroupUtils';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import CommentsModal from '../../../comments/CommentsModal';
import ShowDescriptionDialog from '../../component/ShowDescriptionDialog';
import { convertMarkdownToHtml } from '../../../../utils/markdownToHTML';
import parse from 'html-react-parser';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';
import { usePageLoading } from '../../../../contexts/LoadingContext';
import PageHeader, { PageCard, PageHeaderButton, PageShell } from '../../../common/PageHeader';

// Configure PDF.js worker - use local worker file from public folder
// Ensure public/pdf.worker.min.js matches the pdfjs-dist version (5.3.31)
if (typeof window !== 'undefined') {
    // Use local worker file from public folder
    pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
}

const getPdfPageCount = async (file) => {
    try {
        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = pdfjsLib.getDocument({
            data: arrayBuffer,
            useSystemFonts: true,
            verbosity: 0 // Suppress warnings
        });
        const pdf = await loadingTask.promise;
        return pdf.numPages;
    } catch (error) {
        console.error("PDF parsing error:", error);
        // Re-throw with more context
        throw new Error(`Failed to parse PDF: ${error.message || 'Unknown error'}`);
    }
};

/** Best-effort E.164 from resume extraction (often missing country code). */
const resumePhoneToE164 = (raw, countryCode) => {
    if (raw == null || raw === '') return '';
    const s = String(raw).trim();
    if (!s) return '';

    // If it already starts with +, trust it
    if (s.startsWith('+')) {
        const p = parsePhoneNumberFromString(s);
        if (p?.isValid()) return p.number;
        return s;
    }

    const digits = s.replace(/\D/g, '');
    if (!digits) return '';

    // If we have an extracted country code (e.g. +1, +44)
    if (countryCode && countryCode.startsWith('+')) {
        const codeNoPlus = countryCode.slice(1);

        // 1. Try prepending the country code (unless digits already start with it)
        if (!digits.startsWith(codeNoPlus)) {
            const full = `${countryCode}${digits}`;
            const p = parsePhoneNumberFromString(full);
            if (p?.isValid()) return p.number;
        }

        // 2. Try parsing +digits (handles cases where digits ALREADY included country code)
        let p = parsePhoneNumberFromString(`+${digits}`);
        if (p?.isValid()) return p.number;

        // 3. Last resort fallback
        return `${countryCode}${digits}`;
    }

    // Fallback logic (defaulting to India if 10 digits)
    let p = parsePhoneNumberFromString(s, 'IN');
    if (p?.isValid()) return p.number;

    if (digits.length === 10) return `+91${digits}`;

    p = parsePhoneNumberFromString(`+${digits}`);
    if (p?.isValid()) return p.number;

    return `+${digits}`;
};

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return isMobile;
};

const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

// Small display formatter for candidate joined datetime
const formatJoinedSmall = (dateInput) => {
    if (!dateInput) return '';
    const date = new Date(dateInput);
    if (isNaN(date)) return '';

    const now = new Date();
    const isToday =
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();
    const isCurrentYear = date.getFullYear() === now.getFullYear();

    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });

    if (isToday) {
        return `Today at ${time}`;
    }

    const day = date.getDate();
    const monthShort = date.toLocaleString('en-US', { month: 'short' });
    const yearSuffix = isCurrentYear ? '' : `'${String(date.getFullYear()).slice(-2)}`;

    const ordinal = (n) => {
        const mod10 = n % 10, mod100 = n % 100;
        if (mod10 === 1 && mod100 !== 11) return 'st';
        if (mod10 === 2 && mod100 !== 12) return 'nd';
        if (mod10 === 3 && mod100 !== 13) return 'rd';
        return 'th';
    };

    return `${day}${ordinal(day)} ${monthShort}${yearSuffix} at ${time}`;
};

// Format last login with relative time
// const formatLastLogin = (dateInput) => {
//     if (!dateInput) return { display: 'Never', tooltip: 'Never logged in' };
//     const date = new Date(dateInput);
//     if (isNaN(date)) return { display: 'Never', tooltip: 'Never logged in' };

//     const now = new Date();
//     const diffMs = now - date;
//     const diffMins = Math.floor(diffMs / 60000);
//     const diffHours = Math.floor(diffMs / 3600000);
//     const diffDays = Math.floor(diffMs / 86400000);
//     const diffWeeks = Math.floor(diffDays / 7);
//     const diffMonths = Math.floor(diffDays / 30);
//     const diffYears = Math.floor(diffDays / 365);

//     const isToday =
//         date.getFullYear() === now.getFullYear() &&
//         date.getMonth() === now.getMonth() &&
//         date.getDate() === now.getDate();

//     if (isToday) {
//         if (diffMins < 1) return { display: 'Just now', tooltip: date.toLocaleString() };
//         if (diffMins < 60) return { display: `${diffMins}m ago`, tooltip: date.toLocaleString() };
//         return { display: `${diffHours}h ago`, tooltip: date.toLocaleString() };
//     }

//     if (diffDays === 0) return { display: 'Today', tooltip: date.toLocaleString() };
//     if (diffDays === 1) return { display: 'Yesterday', tooltip: date.toLocaleString() };
//     if (diffDays < 7) return { display: `${diffDays}d ago`, tooltip: date.toLocaleString() };
//     if (diffWeeks < 4) return { display: `${diffWeeks}w ago`, tooltip: date.toLocaleString() };
//     if (diffMonths < 12) return { display: `${diffMonths}mo ago`, tooltip: date.toLocaleString() };
//     return { display: `${diffYears}y ago`, tooltip: date.toLocaleString() };
// };

// Invite Status Badge Component
const InviteStatusBadge = ({ status }) => {
    if (!status) {
        return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                No Invite
            </span>
        );
    }

    const statusConfig = {
        'Active': {
            bg: 'bg-green-100',
            text: 'text-green-700',
            border: 'border-green-200',
            label: 'Active'
        },
        'Used': {
            bg: 'bg-blue-100',
            text: 'text-blue-700',
            border: 'border-blue-200',
            label: 'Used'
        },
        'Expired': {
            bg: 'bg-red-100',
            text: 'text-red-700',
            border: 'border-red-200',
            label: 'Expired'
        }
    };

    const config = statusConfig[status] || statusConfig['Active'];

    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text} border ${config.border}`}>
            {config.label}
        </span>
    );
};

const scoreTone = (score) => {
    if (score === null || score === undefined) return 'text-gray-500';
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-sky-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
};

const RecentInterviewScore = ({ candidate }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const interview = candidate?.recent_interview;
    if (!interview || interview.status !== 'completed') return null;
    const score = interview.effective_score ?? interview.total_score;
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className={`text-xs font-semibold ${scoreTone(score)}`}>
                {score === null || score === undefined ? '—' : `${Number(score).toFixed(2)}%`}
            </span>
            <button
                type="button"
                onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/candidatedetails/${candidate.id}`);
                }}
                className="text-xs font-medium text-sky-600 hover:text-fuchsia-600 hover:underline"
            >
                {t('candidateGroups.more', 'more')}
            </button>
        </span>
    );
};

const INVITE_STATUS_FILTER_OPTIONS = [
    { value: 'none', label: 'No Invite', badgeClass: 'bg-gray-100 text-gray-600 border border-gray-200' },
    { value: 'active', label: 'Active', badgeClass: 'bg-green-100 text-green-700 border border-green-200' },
    { value: 'expired', label: 'Expired', badgeClass: 'bg-red-100 text-red-700 border border-red-200' },
    { value: 'used', label: 'Used', badgeClass: 'bg-blue-100 text-blue-700 border border-blue-200' },
];

// Live Interview Ping Component
const GroupLogos = ({ candidate }) => {
    const navigate = useNavigate();
    const groups = Array.isArray(candidate?.groups) ? candidate.groups : [];
    if (groups.length === 0) return null;
    return (
        <span className="inline-flex items-center gap-1">
            {groups.map((group) => (
                <button
                    key={group.id}
                    type="button"
                    title={group.name}
                    aria-label={group.name}
                    onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/candidate-groups/${group.id}`);
                    }}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-sky-200 bg-sky-50 text-sm leading-none hover:bg-sky-100"
                >
                    {group.logo || '•'}
                </button>
            ))}
        </span>
    );
};

const LiveInterviewPing = ({ candidateId, liveInterviews }) => {
    const hasLiveInterview = liveInterviews[candidateId] && liveInterviews[candidateId].length > 0;

    if (!hasLiveInterview) return null;

    const interview = liveInterviews[candidateId][0]; // Get the first live interview

    return (
        <div className="flex items-center gap-1 group relative">
            <div className="relative">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <div className="absolute inset-0 w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
            </div>
            <span className=" text-green-600">LIVE</span>

            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white  rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                <div className="mb-1">Live Interview</div>
                <div>Job: {interview.job_title || 'N/A'}</div>
                <div>Progress: {interview.progress_percentage?.toFixed(1) || 0}%</div>
                <div>Time Left: {interview.remaining_minutes || 0}m</div>
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
        </div>
    );
};

export default function Candidates({ onLogout }) {
    const { t, i18n } = useTranslation();
    const [isModalOpen, setModalOpen] = useState(false);
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const isRTL = document.documentElement.dir === 'rtl' || i18n.language === 'ar';

    // Custom hooks
    const {
        candidates,
        setCandidates,
        loading,
        totalCandidates,
        totalPages,
        fetchCandidates,
        addCandidate,
        deleteCandidate,
        unarchiveCandidate,
        generateInvite
    } = useCandidates();

    usePageLoading(loading && candidates.length === 0, {
        message: t('common.loading', 'Loading…'),
    });

    const { jobDescriptions } = useJobDescriptions();
    const {
        skillAnalysis,
        setSkillAnalysis,
        analyzeSkills,
        reanalyzeSkills,
        createInviteWithSkills
    } = useSkillAnalysis();

    // States
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [form, setForm] = useState({
        firstName: '', lastName: '',
        email: '', resume: null,
    });
    const [phoneE164, setPhoneE164] = useState('');
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
    const filterDropdownRef = useRef(null);

    const [isAddJdDropdownOpen, setIsAddJdDropdownOpen] = useState(false);
    const addJdDropdownRef = useRef(null);

    const [inviteStatusFilter, setInviteStatusFilter] = useState('');
    const [isInviteStatusDropdownOpen, setIsInviteStatusDropdownOpen] = useState(false);
    const inviteStatusDropdownRef = useRef(null);
    const [groupFilter, setGroupFilter] = useState('');
    const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);
    const groupDropdownRef = useRef(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const addCandidateDropdownRef = useRef(null);
    const [candidateGroups, setCandidateGroups] = useState([]);
    const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
    const [assignGroupId, setAssignGroupId] = useState('');
    const [assigningGroup, setAssigningGroup] = useState(false);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [addCandidateGroupId, setAddCandidateGroupId] = useState('');
    const [addCandidateGroupMode, setAddCandidateGroupMode] = useState('select');
    const [newCandidateGroup, setNewCandidateGroup] = useState({ name: '', description: '', logo: '🌟' });
    const [isAddGroupDropdownOpen, setIsAddGroupDropdownOpen] = useState(false);
    const addGroupDropdownRef = useRef(null);
    const [addGroupSearch, setAddGroupSearch] = useState('');
    const resumeFileInputRef = useRef(null);
    const [isDraggingResume, setIsDraggingResume] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isFilterDropdownOpen && filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
                setIsFilterDropdownOpen(false);
            }
            if (isAddJdDropdownOpen && addJdDropdownRef.current && !addJdDropdownRef.current.contains(event.target)) {
                setIsAddJdDropdownOpen(false);
            }
            if (isAddGroupDropdownOpen && addGroupDropdownRef.current && !addGroupDropdownRef.current.contains(event.target)) {
                setIsAddGroupDropdownOpen(false);
            }
            if (isInviteStatusDropdownOpen && inviteStatusDropdownRef.current && !inviteStatusDropdownRef.current.contains(event.target)) {
                setIsInviteStatusDropdownOpen(false);
            }
            if (isGroupDropdownOpen && groupDropdownRef.current && !groupDropdownRef.current.contains(event.target)) {
                setIsGroupDropdownOpen(false);
            }
            if (dropdownOpen && addCandidateDropdownRef.current && !addCandidateDropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isFilterDropdownOpen, isAddJdDropdownOpen, isAddGroupDropdownOpen, isInviteStatusDropdownOpen, isGroupDropdownOpen, dropdownOpen]);
    const [addCandidateJdTagId, setAddCandidateJdTagId] = useState('');
    const [addCandidateJdSearch, setAddCandidateJdSearch] = useState('');
    const [jdFilterSearch, setJdFilterSearch] = useState('');
    const [jdDescriptionModal, setJdDescriptionModal] = useState({ open: false, title: '', content: null });
    const [jdDescriptionLoading, setJdDescriptionLoading] = useState(false);
    const [mobileError, setMobileError] = useState('');

    const [generatedInvite, setGeneratedInvite] = useState(null);
    const [copiedInviteUrl, setCopiedInviteUrl] = useState(null);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const {
        containerRef: tableViewportRef,
        pageSize,
        pageSizeMode,
        setPageSizeMode,
        pageSizeOptions,
    } = useViewportPageSize({
        enabled: !isMobile,
        rowHeight: 48,
        headerHeight: 42,
        // Pagination footer sits inside the measured card, so reserve it here.
        reservedBottom: 56,
        minSize: 10,
        maxSize: 50,
        initialSize: 10,
        defaultMode: 'auto',
    });
    const skipPageResetOnSizeRef = useRef(true);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [showEmailConfirmModal, setShowEmailConfirmModal] = useState(false);
    const [emailRecipient, setEmailRecipient] = useState(null);
    const [ccRecipients, setCcRecipients] = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [selectedCandidateForInvite, setSelectedCandidateForInvite] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    // Live interview tracking state
    const [liveInterviews] = useState({});
    // Multi-JD scheduling state
    const [showMultiJDModal, setShowMultiJDModal] = useState(false);
    const [selectedCandidateForMultiJD, setSelectedCandidateForMultiJD] = useState(null);
    const [selectedJDs, setSelectedJDs] = useState([]);
    const [isCreatingMultiJDInvites, setIsCreatingMultiJDInvites] = useState(false);
    const [multiJDResults, setMultiJDResults] = useState(null);

    // Comments modal state
    const [showCommentsModal, setShowCommentsModal] = useState(false);
    const [selectedCandidateForComments, setSelectedCandidateForComments] = useState(null);

    // 3-dot action menu state
    const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
    const [selectedActionCandidate, setSelectedActionCandidate] = useState(null);

    const handleOpenActionMenu = (event, candidate) => {
        event.stopPropagation();
        setActionMenuAnchor(event.currentTarget);
        setSelectedActionCandidate(candidate);
    };

    const handleCloseActionMenu = () => {
        setActionMenuAnchor(null);
        setSelectedActionCandidate(null);
    };

    const handleMenuAction = (actionFn, ...args) => {
        handleCloseActionMenu();
        if (typeof actionFn === 'function') {
            actionFn(...args);
        }
    };

    // Interview counts for all candidates
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [expireDialogOpen, setExpireDialogOpen] = useState(false);
    const [expireToken, setExpireToken] = useState(null);
    const [selectedSkills, setSelectedSkills] = useState({}); // Changed to object for JD-specific skills

    // Skill analysis for Multi-JD modal
    const [selectedJDForAnalysis, setSelectedJDForAnalysis] = useState(null);
    const [showSkillAnalysisModal, setShowSkillAnalysisModal] = useState(false);
    const [skillAnalysisLoading, setSkillAnalysisLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [searchQuery, setSearchQuery] = useState('');
    const [copied, setCopied] = useState({ email: {}, phone: {}, inviteUrl: {} });
    const [timelineOpen, setTimelineOpen] = useState(false);

    // Helper: highlight occurrences of searchQuery within a text
    const highlightMatch = (text) => {
        if (!text) return '';
        if (!searchQuery?.trim()) return text;
        try {
            const escaped = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`(${escaped})`, 'ig');
            const parts = String(text).split(regex);
            return (
                <>
                    {parts.map((part, idx) =>
                        regex.test(part) ? (
                            <mark key={idx} className="bg-yellow-200 text-gray-900 px-0.5 rounded">
                                {part}
                            </mark>
                        ) : (
                            <span key={idx}>{part}</span>
                        )
                    )}
                </>
            );
        } catch {
            return text;
        }
    };

    // Helper: truncate candidate name to 40 characters with ellipsis
    const truncateName = (firstName, lastName) => {
        const fullName = `${firstName || ''} ${lastName || ''}`.trim();
        if (fullName.length <= 30) return fullName;
        return fullName.substring(0, 30) + '...';
    };
    const [showArchived, setShowArchived] = useState(false);
    const [jdTagFilter, setJdTagFilter] = useState('');
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [companies, setCompanies] = useState([]);

    const userRole = localStorage.getItem('role');

    useEffect(() => {
        if (userRole === 'Super Admin') {
            const fetchCompaniesList = async () => {
                try {
                    const res = await api.get('companies/');
                    const companyData = res.data.results || res.data;
                    setCompanies(Array.isArray(companyData) ? companyData : []);
                } catch (err) {
                    console.error('Failed to fetch companies:', err);
                }
            };
            fetchCompaniesList();
        }
    }, [userRole]);

    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [isGeneratingInvite, setIsGeneratingInvite] = useState(false);
    const [resumeUpload, setResumeUpload] = useState({
        isUploading: false,
        file: null,
        extractedData: null,
        tempFileInfo: null,
        parseError: null
    });

    // Edit resume state
    const [openEditResumeDialog, setOpenEditResumeDialog] = useState(false);
    const [editingCandidate, setEditingCandidate] = useState(null);
    const [editResumeUpload, setEditResumeUpload] = useState({
        isUploading: false,
        file: null,
        extractedData: null,
        tempFileInfo: null,
        parseError: null
    });
    const [editResumeLoading, setEditResumeLoading] = useState(false);

    // Add Candidate submit state
    const [isAddingCandidate, setIsAddingCandidate] = useState(false);
    const [isMultiParsing, setIsMultiParsing] = useState(false);
    const [parsedBulkCandidates, setParsedBulkCandidates] = useState(null);
    const [bulkUploadId, setBulkUploadId] = useState(null);
    const [bulkTotalFiles, setBulkTotalFiles] = useState(0);
    const activeExtractionTaskId = useRef(null);

    const initialPage = parseInt(searchParams.get("page") || "1", 10);
    const [page, setPage] = useState(initialPage);
    const [bulkUploadFile, setBulkUploadFile] = useState(null);
    const [bulkUploadFiles, setBulkUploadFiles] = useState(null);
    const singleInputRef = useRef(null);
    const bulkInputRef = useRef(null);

    // Close all overlays/modals to prevent stacking.
    // Use this before opening a new modal/dialog.
    const closeAllOverlays = React.useCallback(() => {
        setOpenAddDialog(false);
        setDialogOpen(false);
        setExpireDialogOpen(false);
        setShowEmailConfirmModal(false);
        setShowInviteModal(false);
        setShowMultiJDModal(false);
        setShowSkillAnalysisModal(false);
        setShowCommentsModal(false);
        setOpenEditResumeDialog(false);
        setTimelineOpen(false);
        setDropdownOpen(false);
        setModalOpen(false);
        setIsInviteStatusDropdownOpen(false);
        setIsFilterDropdownOpen(false);
        setIsGroupDropdownOpen(false);
        setActionMenuAnchor(null);
        setSelectedActionCandidate(null);
    }, []);


    //On first load: if no query param AND not coming from details → reset to 1

    useEffect(() => {
        if (!searchParams.get("page") && !location.state?.fromDetails) {
            setPage(1);
            setSearchParams({ page: "1" });
        }
    },// eslint-disable-next-line react-hooks/exhaustive-deps
        []);

    //Keep query params in sync when page changes
    useEffect(() => {
        setSearchParams({ page: page.toString() });
    }, [page, setSearchParams]);

    // When viewport-driven page size changes, return to page 1 (skip the initial mount
    // so deep links like ?page=3 are preserved).
    useEffect(() => {
        if (skipPageResetOnSizeRef.current) {
            skipPageResetOnSizeRef.current = false;
            return;
        }
        setPage(1);
    }, [pageSize]);

    //Fetch candidates when params change
    useEffect(() => {
        fetchCandidates(page, pageSize, searchQuery, showArchived, jdTagFilter || null, selectedCompanyId, inviteStatusFilter || null, groupFilter || null);
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [page, pageSize, searchQuery, showArchived, jdTagFilter, selectedCompanyId, inviteStatusFilter, groupFilter]);

    useEffect(() => {
        const params = { page_size: 100, ordering: 'name' };
        if (selectedCompanyId && selectedCompanyId !== 'b2c') {
            params.company = selectedCompanyId;
        }
        api.get('candidate-groups/', { params })
            .then((res) => {
                const results = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setCandidateGroups(results);
            })
            .catch(() => setCandidateGroups([]));
    }, [selectedCompanyId]);

    useEffect(() => {
        setSelectedCandidateIds([]);
    }, [page, searchQuery, showArchived, jdTagFilter, selectedCompanyId, inviteStatusFilter, groupFilter]);

    const toggleCandidateSelection = (candidateId) => {
        setSelectedCandidateIds((current) => (
            current.includes(candidateId)
                ? current.filter((id) => id !== candidateId)
                : [...current, candidateId]
        ));
    };

    const assignSelectedToGroup = async () => {
        if (!assignGroupId || selectedCandidateIds.length === 0) return;
        setAssigningGroup(true);
        try {
            const response = await api.post(`candidate-groups/${assignGroupId}/members/`, {
                candidate_ids: selectedCandidateIds,
            });
            const added = response.data?.added?.length || 0;
            const already = response.data?.already_members?.length || 0;
            const rejected = response.data?.rejected?.length || 0;
            const parts = [`${added} added`];
            if (already) parts.push(`${already} already in the group`);
            if (rejected) parts.push(`${rejected} could not be added`);
            setSnackbar({
                open: true,
                message: parts.join(', '),
                severity: rejected && !added ? 'error' : 'success',
            });
            setAssignDialogOpen(false);
            setSelectedCandidateIds([]);
            setAssignGroupId('');
            fetchCandidates(page, pageSize, searchQuery, showArchived, jdTagFilter || null, selectedCompanyId, inviteStatusFilter || null, groupFilter || null);
        } catch (error) {
            const detail = error.response?.data?.detail || error.response?.data?.name || 'Failed to add candidates to the group';
            setSnackbar({ open: true, message: typeof detail === 'string' ? detail : 'Failed to add candidates to the group', severity: 'error' });
        } finally {
            setAssigningGroup(false);
        }
    };

    // Handlers
    const handleCloseAddDialog = () => {
        setOpenAddDialog(false);
        setForm({ firstName: '', lastName: '', email: '', resume: null });
        setPhoneE164('');
        setAddCandidateJdTagId('');
        setAddCandidateJdSearch('');
        setIsAddJdDropdownOpen(false);
        setAddCandidateGroupId('');
        setIsAddGroupDropdownOpen(false);
        setAddGroupSearch('');
        setAddCandidateGroupMode('select');
        setNewCandidateGroup({ name: '', description: '', logo: '🌟' });
        setMobileError('');
        setResumeUpload({
            isUploading: false,
            file: null,
            extractedData: null,
            tempFileInfo: null,
            parseError: null
        });
        if (resumeFileInputRef.current) resumeFileInputRef.current.value = '';
    };

    const handleResumeFileSelect = async (file) => {
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            setSnackbar({
                open: true,
                message: 'Resume file size cannot exceed 10 MB.',
                severity: 'error'
            });
            if (resumeFileInputRef.current) resumeFileInputRef.current.value = '';
            return;
        }

        if (file.type === 'application/pdf') {
            try {
                const pageCount = await getPdfPageCount(file);
                if (pageCount > 10) {
                    setSnackbar({
                        open: true,
                        message: 'PDF resumes cannot exceed 10 pages.',
                        severity: 'error'
                    });
                    if (resumeFileInputRef.current) resumeFileInputRef.current.value = '';
                    return;
                }
            } catch (error) {
                console.error("Failed to parse PDF:", error);
                const errorMessage = error.message || 'Unknown error';
                setSnackbar({
                    open: true,
                    message: `Could not read the PDF file to check the page count. ${errorMessage.includes('worker') ? 'Please refresh the page and try again.' : ''}`,
                    severity: 'error'
                });
                if (resumeFileInputRef.current) resumeFileInputRef.current.value = '';
                return;
            }
        }

        setForm(prev => ({ ...prev, resume: file }));

        if (resumeUpload.tempFileInfo) {
            setSnackbar({
                open: true,
                message: `Will use "${file.name}" instead of the attached resume`,
                severity: 'info'
            });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleAddCandidate = async (e) => {
        e.preventDefault();

        // Validation
        if (!form.firstName?.trim()) {
            setSnackbar({ open: true, message: 'First name is required', severity: 'error' });
            return;
        }
        if (!form.lastName?.trim()) {
            setSnackbar({ open: true, message: 'Last name is required', severity: 'error' });
            return;
        }
        if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            setSnackbar({ open: true, message: 'Please enter a valid email address', severity: 'error' });
            return;
        }
        // Check if we have either a form resume or temp file from upload
        if (!form.resume && !resumeUpload.tempFileInfo) {
            setSnackbar({ open: true, message: 'Please upload a resume', severity: 'error' });
            return;
        }
        if (!phoneE164?.trim()) {
            setMobileError('Phone number is required');
            return;
        }
        const parsedPhone = parsePhoneNumberFromString(phoneE164);
        if (!parsedPhone || !isValidPhoneNumber(phoneE164)) {
            setMobileError('Enter a valid phone number for the selected country');
            return;
        }

        const formData = new FormData();
        Object.entries(form).forEach(([key, value]) => {
            // Skip resume if we have a temp file and no new file was selected
            if (key === 'resume' && !value && resumeUpload.tempFileInfo) {
                return; // Don't append empty resume when we have temp file
            }
            if (value) formData.append(key, value);
        });
        formData.append('countryCode', `+${parsedPhone.countryCallingCode}`);
        formData.append('mobile', parsedPhone.nationalNumber);

        // Add temporary file information if available and no new resume was uploaded
        if (resumeUpload.tempFileInfo && !form.resume) {
            formData.append('temp_file_path', resumeUpload.tempFileInfo.temp_path);
            formData.append('temp_file_original_name', resumeUpload.tempFileInfo.original_name);
        }

        try {
            setIsAddingCandidate(true);
            let targetGroupId = addCandidateGroupId;

            // If in create mode and group name provided, create the group first
            if (addCandidateGroupMode === 'create' && (newCandidateGroup.name || '').trim()) {
                try {
                    const groupCompany = newCandidateGroup.company || (selectedCompanyId && selectedCompanyId !== 'b2c' ? selectedCompanyId : null);
                    const createdGroup = await createCandidateGroup({
                        name: newCandidateGroup.name,
                        description: newCandidateGroup.description,
                        logo: newCandidateGroup.logo,
                        companyId: groupCompany,
                    });
                    targetGroupId = String(createdGroup.id);
                    setCandidateGroups((prev) => [createdGroup, ...prev.filter((g) => g.id !== createdGroup.id)]);
                } catch (grpCreateErr) {
                    const msg = grpCreateErr.response?.data?.name?.[0]
                        || grpCreateErr.response?.data?.detail
                        || grpCreateErr.response?.data?.error
                        || grpCreateErr.message
                        || 'Failed to create candidate group.';
                    setSnackbar({ open: true, message: msg, severity: 'error' });
                    setIsAddingCandidate(false);
                    return;
                }
            }

            const response = await addCandidate(formData);
            const candidateId = response?.data?.candidate_id;
            if (candidateId && addCandidateJdTagId) {
                const token = localStorage.getItem('adminToken');
                try {
                    await api.post(`admin/candidate/${candidateId}/jd-tags/`, { job_description_id: parseInt(addCandidateJdTagId, 10) }, {
                        headers: { Authorization: `Token ${token}` },
                    });
                } catch (jdErr) {
                    console.warn('Failed to add JD tag:', jdErr);
                    setSnackbar({
                        open: true,
                        message: jdErr.response?.data?.error || 'Candidate added; JD tag could not be attached.',
                        severity: 'warning'
                    });
                }
            }
            if (candidateId && targetGroupId) {
                try {
                    await addCandidatesToGroup(targetGroupId, [candidateId]);
                } catch (groupAssignErr) {
                    console.warn('Failed to assign candidate to group:', groupAssignErr);
                    setSnackbar({
                        open: true,
                        message: groupAssignErr.response?.data?.detail || 'Candidate added, but could not be added to the candidate group.',
                        severity: 'warning'
                    });
                }
            }
            setOpenAddDialog(false);
            setForm({ firstName: '', lastName: '', email: '', resume: null });
            setPhoneE164('');
            setAddCandidateJdTagId('');
            setAddCandidateGroupId('');
            setAddCandidateGroupMode('select');
            setNewCandidateGroup({ name: '', description: '', logo: '🌟' });
            setMobileError('');
            // Clear resume upload state
            setResumeUpload({
                isUploading: false,
                file: null,
                extractedData: null,
                tempFileInfo: null,
                parseError: null
            });
            fetchCandidates(page, pageSize, searchQuery, showArchived, jdTagFilter || null, selectedCompanyId, inviteStatusFilter || null, groupFilter || null);
            setSnackbar({ open: true, message: 'Candidate added successfully', severity: 'success' });
        } catch (err) {
            const rawError = err.response?.data?.error || 'Failed to add candidate';
            const backendErrorKeyMap = {
                'Invalid email format.': 'bulkUpload.invalidEmailFormat',
                'This user is already a candidate in your company.': 'bulkUpload.candidateAlreadyExists',
                'Candidate with this email already exists.': 'bulkUpload.emailAlreadyExists',
                'All fields are required.': 'bulkUpload.allFieldsRequired',
                'Aadhaar number must be 12 digits.': 'bulkUpload.aadhaarInvalid',
            };
            const translationKey = backendErrorKeyMap[rawError];
            const translatedError = translationKey ? t(translationKey) : rawError;
            setSnackbar({
                open: true,
                message: translatedError,
                severity: 'error'
            });
            // Close modal on error as per requirement
            setOpenAddDialog(false);
            // Reset form and upload state
            setForm({ firstName: '', lastName: '', email: '', resume: null });
            setPhoneE164('');
            setAddCandidateJdTagId('');
            setAddCandidateGroupId('');
            setAddCandidateGroupMode('select');
            setNewCandidateGroup({ name: '', description: '', logo: '🌟' });
            setMobileError('');
            setResumeUpload({
                isUploading: false,
                file: null,
                extractedData: null,
                tempFileInfo: null,
                parseError: null
            });
        } finally {
            setIsAddingCandidate(false);
        }
    };

    const handleConfirmInvite = async (candidateId, jobDescriptionId, languageCode = null, scheduleConfig = null) => {
        setIsGeneratingInvite(true);
        try {
            const response = await generateInvite(candidateId, jobDescriptionId, languageCode, scheduleConfig);
            const data = response.data;
            setGeneratedInvite({ ...data, interviewUrl: getUpdatedLink(data.interviewUrl) });

            setCandidates(prev =>
                prev.map(c =>
                    c.id === candidateId
                        ? {
                            ...c,
                            inviteStatus: 'Active',
                            inviteUrl: getUpdatedLink(response?.data?.interviewUrl),
                            inviteToken: response?.data?.interviewUrl?.split('/').pop() // Extract token from URL
                        }
                        : c
                )
            );

            setSnackbar({ open: true, message: 'Invite generated successfully', severity: 'success' });
        } catch (err) {
            console.error('Error generating invite:', err);

            let errorMessage = 'Failed to generate interview invite. Please try again.';
            let isCreditsError = false;

            // Handle different types of errors
            if (err.response?.status === 402) {
                // HTTP 402 Payment Required - insufficient credits
                const creditsRemaining = err.response?.data?.credits_remaining || 0;
                errorMessage = err.response?.data?.error || 'Insufficient credits to generate interview invite.';
                errorMessage += ` You have ${creditsRemaining} credits remaining.`;
                isCreditsError = true;
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
                // Also check for credit-related text in other error messages
                isCreditsError = errorMessage.toLowerCase().includes('credit');
            }

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
                autoHideDuration: isCreditsError ? 8000 : 6000 // Longer duration for credit errors
            });

            throw err; // Re-throw to let modal handle the loading state
        } finally {
            setIsGeneratingInvite(false);
        }
    };

    const handleGenerateInviteWithSkills = async (result, candidateId) => {
        setGeneratedInvite(result.data);

        setCandidates(prev =>
            prev.map(c =>
                c.id === candidateId
                    ? {
                        ...c,
                        inviteStatus: 'Active',
                        inviteUrl: getUpdatedLink(result.data.interviewUrl),
                        inviteToken: result.data.interviewUrl?.split('/').pop(), // Extract token from URL
                    }
                    : c
            )
        );
    };

    const getUpdatedLink = (link) => {
        if (!link) return '';
        const url = new URL(link);
        const frontendUrl = (process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000').replace(/\/$/, '');
        return frontendUrl + url.pathname;
    };

    const copyToClipboard = (inviteUrl) => {
        navigator.clipboard.writeText(inviteUrl);
        setCopiedInviteUrl(inviteUrl);
        setTimeout(() => setCopiedInviteUrl(null), 2000);
    };

    const handleConfirmExpireInvite = async () => {
        try {
            await expireInvite(expireToken);
            setSnackbar({
                open: true,
                message: 'Invite expired successfully',
                severity: 'success'
            });
            fetchCandidates(page, pageSize, searchQuery, showArchived, jdTagFilter || null, selectedCompanyId, inviteStatusFilter || null, groupFilter || null); // Refresh data
        } catch (err) {
            console.error('Error expiring invite:', err);
            setSnackbar({
                open: true,
                message: 'Failed to expire invite',
                severity: 'error'
            });
        } finally {
            setExpireDialogOpen(false);
            setExpireToken(null);
        }
    };

    const handleDeleteOpen = (id) => {
        setDeleteId(id);
        setDialogOpen(true);
    };

    const handleDeleteCandidate = async () => {
        try {
            await deleteCandidate(deleteId);
            fetchCandidates(page, pageSize, searchQuery, showArchived, jdTagFilter || null, selectedCompanyId, inviteStatusFilter || null, groupFilter || null);
            setDialogOpen(false);
            setSnackbar({ open: true, message: 'Candidate archived successfully', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to archive candidate', severity: 'error' });
        }
    };

    const handleUnarchiveCandidate = async (candidateId) => {
        try {
            await unarchiveCandidate(candidateId);
            fetchCandidates(page, pageSize, searchQuery, showArchived, jdTagFilter || null, selectedCompanyId, inviteStatusFilter || null, groupFilter || null);
            setSnackbar({ open: true, message: 'Candidate unarchived successfully', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to unarchive candidate', severity: 'error' });
        }
    };


    const sendEmail = async (feedback, sessionId, candidateEmail, candidateName, htmlContent = null, filename = null) => {
        setEmailRecipient({
            name: candidateName,
            email: candidateEmail,
            htmlContent: htmlContent,
            filename: filename
        });
        setShowEmailConfirmModal(true);
        setShowFeedbackModal(false);
    };

    const handleMultipleResumeUpload = async (files) => {
        setIsMultiParsing(true);

        setSnackbar({ open: true, message: `Parsing ${files.length} resumes...`, severity: 'success' });

        const validFiles = [];
        for (const file of files) {
            const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(file.type)) {
                setSnackbar({ open: true, message: `Skipping invalid file type: ${file.name}`, severity: 'warning' });
                continue;
            }

            if (file.type === 'application/pdf') {
                try {
                    const pageCount = await getPdfPageCount(file);
                    if (pageCount > 10) {
                        setSnackbar({ open: true, message: `PDF resumes cannot exceed 10 pages. Skipping ${file.name}.`, severity: 'error' });
                        continue;
                    }
                } catch (error) {
                    setSnackbar({ open: true, message: `Could not read PDF file ${file.name}. Skipping.`, severity: 'error' });
                    continue;
                }
            }

            const maxSize = 10 * 1024 * 1024;
            if (file.size > maxSize) {
                setSnackbar({ open: true, message: `File size must be less than 10MB. Skipping ${file.name}.`, severity: 'error' });
                continue;
            }
            validFiles.push(file);
        }

        if (validFiles.length === 0) {
            setSnackbar({ open: true, message: 'No valid resumes to upload.', severity: 'warning' });
            setIsMultiParsing(false);
            return;
        }

        const formData = new FormData();
        validFiles.forEach(file => {
            formData.append('resumes', file);
        });

        try {
            const response = await api.post('admin/parse-resume/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Token ${localStorage.getItem('token')}`,
                },
            });

            if (response.data.task_id) {
                const { task_id, upload_id, total_files, skipped = [] } = response.data;
                const taskId = task_id;
                const totalFiles = total_files;

                setBulkUploadId(upload_id);
                setBulkTotalFiles(totalFiles);
                activeExtractionTaskId.current = taskId;

                // If there were skipped files (e.g. wrong type/size), add them as failures
                if (skipped.length > 0) {
                    const failedEntries = skipped.map(s => ({
                        source_filename: s.temp_path || `skipped_${Date.now()}_${s.original_name}`,
                        original_name: s.original_name,
                        firstName: "",
                        lastName: "",
                        email: "",
                        phone: "",
                        status: "failed",
                        error: s.error
                    }));
                    setParsedBulkCandidates(prev => [...(prev || []), ...failedEntries]);
                }

                // If we have an upload_id immediately, open the modal to handle it via WS
                setIsMultiParsing(false);
                if (upload_id) {
                    setModalOpen(true);
                } else {
                    pollExtractionStatusForUnified(taskId, true);
                }
            } else {
                // Fallback for immediate results
                const results = response.data;
                processParsingResults(results, true);
                setIsMultiParsing(false);
            }

        } catch (error) {
            console.error('Error uploading and parsing resumes:', error);
            setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to upload and parse resumes', severity: 'error' });
            setIsMultiParsing(false);
        }

    };

    const pollExtractionStatusForUnified = async (taskId, isMulti = false) => {
        if (activeExtractionTaskId.current !== taskId) return;

        try {
            const res = await api.get(`/admin/bulk-extraction-status/${taskId}/`);
            const data = res.data;

            if (activeExtractionTaskId.current !== taskId) return;

            if (data.status === "completed") {
                processParsingResults(data.results, isMulti);
                activeExtractionTaskId.current = null;
                if (isMulti) setIsMultiParsing(false);
                else setResumeUpload(prev => ({ ...prev, isUploading: false }));
            } else {
                // Poll again in 1.5 seconds
                setTimeout(() => pollExtractionStatusForUnified(taskId, isMulti), 1500);
            }
        } catch (err) {
            console.error("Polling error:", err);
            if (activeExtractionTaskId.current === taskId) {
                activeExtractionTaskId.current = null;
                if (isMulti) setIsMultiParsing(false);
                else setResumeUpload(prev => ({ ...prev, isUploading: false }));
                setSnackbar({ open: true, message: 'Connection lost while parsing. Please check your internet.', severity: 'error' });
            }
        }
    };

    const processParsingResults = (results, isMulti) => {
        const parsedCandidates = [];
        let successCount = 0;
        let failedCount = 0;

        results.forEach(item => {
            if (item.parsing_success) {
                const { extracted, temp_file_info } = item;
                const completed = extracted.first_name && extracted.last_name && extracted.email && extracted.mobile;

                parsedCandidates.push({
                    source_filename: temp_file_info.temp_path,
                    original_name: temp_file_info.original_name,
                    firstName: extracted.first_name || "",
                    lastName: extracted.last_name || "",
                    email: extracted.email || "",
                    phone: extracted.mobile || "",
                    countryCode: extracted.country_code || "+91",
                    status: completed ? "ready" : "incomplete",
                    size: temp_file_info.size
                });
                successCount++;
            } else {
                failedCount++;
                setSnackbar({ open: true, message: `Failed to parse ${item.original_name}: ${item.parsing_error || item.error}`, severity: 'error' });
            }
        });

        if (successCount > 0) {
            setSnackbar({ open: true, message: `Successfully parsed ${successCount} resume(s).`, severity: 'success' });

            if (isMulti) {
                setParsedBulkCandidates(parsedCandidates);
                setModalOpen(true);
            } else if (parsedCandidates.length > 0) {
                const firstResult = parsedCandidates[0];
                setResumeUpload(prev => ({
                    ...prev,
                    isUploading: false,
                    extractedData: {
                        first_name: firstResult.firstName,
                        last_name: firstResult.lastName,
                        email: firstResult.email,
                        mobile: firstResult.phone,
                        country_code: firstResult.countryCode || "+91"
                    },
                    tempFileInfo: {
                        temp_path: firstResult.source_filename,
                        original_name: firstResult.original_name,
                        size: firstResult.size
                    },
                    parseError: null
                }));

                // Pre-fill the form
                setForm(prev => ({
                    ...prev,
                    firstName: firstResult.firstName,
                    lastName: firstResult.lastName,
                    email: firstResult.email,
                    resume: null
                }));
                setPhoneE164(resumePhoneToE164(firstResult.phone, firstResult.countryCode));

                setOpenAddDialog(true);
            }
        }

        if (failedCount > 0 && isMulti) {
            setSnackbar({ open: true, message: `Failed to parse ${failedCount} resume(s).`, severity: 'error' });
        }
    };

    const handleUnifiedUpload = async (event) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        if (files.length > 1) {
            // Open modal immediately for multiple files to show progress
            setBulkUploadFiles(Array.from(files));
            setModalOpen(true);
            event.target.value = '';
            return;
        }

        const file = files[0];
        if (!file) return;

        const allowedTypes = [
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/zip',
            'application/x-zip-compressed'
        ];

        const isZipFile = file.type === 'application/zip' ||
            file.type === 'application/x-zip-compressed' ||
            file.name.toLowerCase().endsWith('.zip');

        if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.zip')) {
            setSnackbar({
                open: true,
                message: 'Please upload a PDF, DOCX, or ZIP file only',
                severity: 'error'
            });
            event.target.value = '';
            return;
        }

        if (isZipFile) {
            const MAX_ZIP_SIZE = 100 * 1024 * 1024; // 100MB
            if (file.size > MAX_ZIP_SIZE) {
                setSnackbar({
                    open: true,
                    message: 'ZIP file size exceeds 100 MB limit',
                    severity: 'error'
                });
                event.target.value = '';
                return;
            }
            setBulkUploadFile(file);
            setModalOpen(true);
        } else {
            // For single PDF/DOCX, continue with existing handleResumeUpload logic
            // which also uses the 'resumes' key now.
            // We can wrap it in an array to be consistent with the backend expecting 'resumes' getlist
            await handleResumeUpload(event);
        }

        event.target.value = '';
    };


    const handleResumeUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowedTypes.includes(file.type)) {
            setSnackbar({
                open: true,
                message: 'Please upload a PDF or DOCX file only',
                severity: 'error'
            });
            return;
        }

        if (file.type === 'application/pdf') {
            try {
                const pageCount = await getPdfPageCount(file);
                if (pageCount > 10) {
                    setSnackbar({
                        open: true,
                        message: 'PDF resumes cannot exceed 10 pages.',
                        severity: 'error'
                    });
                    return;
                }
            } catch (error) {
                console.error("Failed to parse PDF:", error);
                const errorMessage = error.message || 'Unknown error';
                console.error("PDF parsing error details:", errorMessage);
                setSnackbar({
                    open: true,
                    message: `Could not read the PDF file to check the page count. ${errorMessage.includes('worker') ? 'Please refresh the page and try again.' : ''}`,
                    severity: 'error'
                });
                return;
            }
        }

        // Validate file size (10MB)
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            setSnackbar({
                open: true,
                message: 'File size must be less than 10MB',
                severity: 'error'
            });
            return;
        }

        setResumeUpload(prev => ({ ...prev, isUploading: true, file, parseError: null }));

        try {
            const formData = new FormData();
            formData.append('resumes', file);

            const response = await api.post('admin/parse-resume/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Token ${localStorage.getItem('token')}`,
                },
            });

            if (response.data.task_id) {
                const taskId = response.data.task_id;
                activeExtractionTaskId.current = taskId;
                pollExtractionStatusForUnified(taskId, false);
            } else if (Array.isArray(response.data) && response.data.length > 0) {
                // Synchronous response
                processParsingResults(response.data, false);
                setResumeUpload(prev => ({ ...prev, isUploading: false }));
            } else {
                setResumeUpload(prev => ({ ...prev, isUploading: false }));
            }

        } catch (error) {
            console.error('Error uploading resume:', error);
            setResumeUpload(prev => ({ ...prev, isUploading: false }));
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to upload and parse resume',
                severity: 'error'
            });
        }

        // Reset file input
        event.target.value = '';
    };

    const handleConfirmEmail = async () => {
        if (!emailRecipient) return;

        setIsSendingEmail(true);
        try {
            const requestData = {
                // sessionId: sessionData?.id,
                candidateEmail: emailRecipient.email,
                candidateName: emailRecipient.name,
                // feedback,
                ccRecipients: ccRecipients.split(',').map(email => email.trim()).filter(email => email)
            };

            if (emailRecipient.htmlContent && emailRecipient.filename) {
                requestData.html_content = emailRecipient.htmlContent;
                requestData.filename = emailRecipient.filename;
            }

            const response = await api.post('admin/send-feedback-email/', requestData, {
                headers: {
                    'Authorization': `Token ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
            });

            if (response.data) {
                setEmailSent(true);
                setTimeout(() => setEmailSent(false), 3000);
                setShowEmailConfirmModal(false);
                setCcRecipients('');

                const message = emailRecipient.htmlContent
                    ? 'Email sent successfully with PDF report'
                    : 'Email sent successfully';
                setSnackbar({ open: true, message, severity: 'success' });
            }
        } catch (error) {
            console.error('Error sending email:', error);
            setSnackbar({
                open: true,
                message: 'Failed to send email. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsSendingEmail(false);
        }
    };

    const handleCandidateDetails = (data) => {
        navigate(`/candidatedetails/${data?.id}`, {
            state: { fromDetails: true },
        });
    };
    // Multi-JD scheduling handlers
    const handleMultiJDSchedule = (candidate) => {
        setSelectedCandidateForMultiJD(candidate);
        setSelectedJDs([]);
        setMultiJDResults(null);
        setShowMultiJDModal(true);
    };

    const handleCreateMultiJDInvites = async (languageCode = null, scheduleConfig = null) => {
        if (!selectedCandidateForMultiJD || selectedJDs.length === 0) {
            setSnackbar({
                open: true,
                message: t('candidateDetails.multiJDSchedule.selectAtLeastOneJd'),
                severity: 'error'
            });
            return;
        }

        setIsCreatingMultiJDInvites(true);

        try {
            const payload = {
                candidateId: selectedCandidateForMultiJD.id,
                jobDescriptionIds: selectedJDs,
                sendEmail: true
            };
            if (languageCode) {
                payload.language_code = languageCode;
            }
            if (scheduleConfig?.global) {
                payload.max_no_of_questions = scheduleConfig.global.max_no_of_questions;
                payload.interview_duration_minutes = scheduleConfig.global.interview_duration_minutes;
                payload.require_audio_verification = scheduleConfig.global.require_audio_verification;
                payload.max_no_of_questions_by_jd = Object.fromEntries(
                    Object.entries(scheduleConfig.overrides || {}).map(([jdId, cfg]) => [jdId, cfg.max_no_of_questions])
                );
                payload.interview_duration_minutes_by_jd = Object.fromEntries(
                    Object.entries(scheduleConfig.overrides || {}).map(([jdId, cfg]) => [jdId, cfg.interview_duration_minutes])
                );
            }
            const response = await api.post('/admin/generate-multi-jd-invites/', payload);

            setMultiJDResults(response.data);
            setSnackbar({
                open: true,
                message: t('candidateDetails.multiJDSchedule.createSuccess', {
                    count: response.data.totalSuccessful,
                    name: response.data.candidateName
                }),
                severity: 'success'
            });

            // Refresh candidates list to update invite statuses
            fetchCandidates(page, pageSize, searchQuery, showArchived, jdTagFilter || null, selectedCompanyId, inviteStatusFilter || null, groupFilter || null);

        } catch (error) {
            console.error('Error creating multi-JD invites:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || t('candidateDetails.multiJDSchedule.createFailed'),
                severity: 'error'
            });
        } finally {
            setIsCreatingMultiJDInvites(false);
        }
    };

    const handleCreateMultiJDInvitesWithSkills = async (languageCode = null, scheduleConfig = null) => {
        if (!selectedCandidateForMultiJD || selectedJDs.length === 0) {
            setSnackbar({
                open: true,
                message: t('candidateDetails.multiJDSchedule.selectAtLeastOneJd'),
                severity: 'error'
            });
            return;
        }

        setIsCreatingMultiJDInvites(true);

        try {
            const results = [];
            let totalSuccessful = 0;
            let totalFailed = 0;

            for (const jdId of selectedJDs) {
                try {
                    const selectedSkillsForJD = selectedSkills[jdId] || [];
                    const response = await createInviteWithSkills(
                        selectedCandidateForMultiJD.id,
                        jdId,
                        selectedSkillsForJD,
                        languageCode,
                        scheduleConfig
                            ? {
                                ...(scheduleConfig.global || {}),
                                ...(scheduleConfig.overrides?.[jdId] || {}),
                                require_audio_verification: scheduleConfig.global?.require_audio_verification
                            }
                            : null
                    );

                    const jd = jobDescriptions.find(jd => jd.id === jdId);
                    results.push({
                        jobDescriptionTitle: jd?.title || t('candidateDetails.multiJDSchedule.jdFallbackTitle', { id: jdId }),
                        interviewUrl: response.data.interviewUrl,
                        status: 'created'
                    });
                    totalSuccessful++;
                } catch (error) {
                    console.error(`Error creating invite for JD ${jdId}:`, error);
                    const jd = jobDescriptions.find(jd => jd.id === jdId);
                    results.push({
                        jobDescriptionTitle: jd?.title || t('candidateDetails.multiJDSchedule.jdFallbackTitle', { id: jdId }),
                        error: error.response?.data?.error || t('candidateDetails.multiJDSchedule.inviteCreateFailed')
                    });
                    totalFailed++;
                }
            }

            setMultiJDResults({
                candidateName: selectedCandidateForMultiJD.firstName + ' ' + selectedCandidateForMultiJD.lastName,
                totalSuccessful,
                totalFailed,
                successfulInvites: results.filter(r => !r.error),
                failedInvites: results.filter(r => r.error)
            });

            setSnackbar({
                open: true,
                message: t('candidateDetails.multiJDSchedule.createWithSkillsSuccess', {
                    count: totalSuccessful,
                    name: `${selectedCandidateForMultiJD.firstName} ${selectedCandidateForMultiJD.lastName}`
                }),
                severity: 'success'
            });

            // Refresh candidates list to update invite statuses
            fetchCandidates(page, pageSize, searchQuery, showArchived, jdTagFilter || null, selectedCompanyId, inviteStatusFilter || null, groupFilter || null);

        } catch (error) {
            console.error('Error creating multi-JD invites with skills:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || t('candidateDetails.multiJDSchedule.createWithSkillsFailed'),
                severity: 'error'
            });
        } finally {
            setIsCreatingMultiJDInvites(false);
        }
    };

    // Skill analysis handlers for Multi-JD modal
    const handleSkillAnalysis = async (jobDescription) => {
        setSelectedJDForAnalysis(jobDescription);
        setShowSkillAnalysisModal(true);
        setSkillAnalysisLoading(true);

        try {
            if (!selectedCandidateForMultiJD?.id || !jobDescription?.id) {
                throw new Error('Missing candidate or job description');
            }
            const data = await analyzeSkills(selectedCandidateForMultiJD.id, jobDescription.id);
            setSkillAnalysis(data);
            setSnackbar({
                open: true,
                message: 'Skill analysis completed successfully!',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error analyzing skills:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to analyze skills. Please try again.',
                severity: 'error'
            });
            setShowSkillAnalysisModal(false);
            setSelectedJDForAnalysis(null);
        } finally {
            setSkillAnalysisLoading(false);
        }
    };

    const handleClearCachesAndRestart = async () => {
        if (!selectedCandidateForMultiJD?.id || !selectedJDForAnalysis?.id) {
            return;
        }
        setSkillAnalysisLoading(true);
        try {
            const data = await analyzeSkills(
                selectedCandidateForMultiJD.id,
                selectedJDForAnalysis.id
            );
            setSkillAnalysis(data);
            setSnackbar({
                open: true,
                message:
                    'Caches cleared and skill analysis completed successfully!',
                severity: 'success',
            });
        } catch (error) {
            console.error('Error re-analyzing after cache clear:', error);
            setSnackbar({
                open: true,
                message:
                    error.message ||
                    'Failed to analyze skills after clearing caches.',
                severity: 'error',
            });
        } finally {
            setSkillAnalysisLoading(false);
        }
    };

    const handleApplySkillsAndReturn = () => {
        setShowSkillAnalysisModal(false);
        setSelectedJDForAnalysis(null);
        setSkillAnalysis(null);
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const sortedCandidates = React.useMemo(() => {
        if (!sortConfig.key) return candidates;
        return [...candidates].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];
            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();
            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [candidates, sortConfig]);

    const visibleCandidateIds = sortedCandidates.map((candidate) => candidate.id);
    const allVisibleSelected = visibleCandidateIds.length > 0 && visibleCandidateIds.every((id) => selectedCandidateIds.includes(id));
    const toggleVisibleSelection = () => {
        setSelectedCandidateIds((current) => {
            if (allVisibleSelected) {
                return current.filter((id) => !visibleCandidateIds.includes(id));
            }
            return Array.from(new Set([...current, ...visibleCandidateIds]));
        });
    };

    // Render helpers
    const SortButton = ({ column, children }) => (
        <button
            onClick={() => handleSort(column)}
            className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors"
        >
            {children}
            {sortConfig.key === column ? (
                sortConfig.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
            ) : (
                <ChevronUp size={12} className="opacity-30" />
            )}
        </button>
    );

    // Edit resume handlers
    const handleEditResume = (candidate) => {
        // Ensure only this modal is open.
        closeAllOverlays();
        setEditingCandidate(candidate);
        setEditResumeUpload({
            isUploading: false,
            file: null,
            extractedData: null,
            tempFileInfo: null,
            parseError: null
        });
        setOpenEditResumeDialog(true);
    };

    const handleEditResumeUpload = async (event) => {
        const file = event.target.files[0];
        console.log('File selected:', file);
        if (!file) return;

        setEditResumeUpload(prev => ({ ...prev, isUploading: true }));

        try {
            const formData = new FormData();
            // Use 'resumes' key to match backend's getlist expectation used elsewhere
            formData.append('resumes', file);

            const response = await api.post('/admin/parse-resume/', formData, {
                headers: {
                    'Authorization': `Token ${localStorage.getItem('token')}`
                }
                // Do not set 'Content-Type' manually; let the browser/axios set the boundary
            });

            const resData = Array.isArray(response.data) ? response.data[0] : response.data;
            const { extracted, temp_file_info, parsing_success } = resData || {};

            setEditResumeUpload({
                isUploading: false,
                file,
                extractedData: extracted,
                tempFileInfo: {
                    path: temp_file_info?.temp_path,
                    original_name: temp_file_info?.original_name
                },
                parseError: parsing_success ? null : 'Parsing failed'
            });

            // Note: We're not pre-filling form data since this is just for resume upload
            // The parsing is still done for potential future use

        } catch (error) {
            console.error('Error uploading resume:', error);
            setEditResumeUpload(prev => ({ ...prev, isUploading: false }));
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to upload and parse resume',
                severity: 'error'
            });
        }

        // Don't reset file input - we need it for form submission
    };

    const handleUpdateResume = async (e) => {
        e.preventDefault();

        // Client-side validation
        if (!editResumeUpload.file) {
            setSnackbar({
                open: true,
                message: 'Please select a resume file before updating.',
                severity: 'error'
            });
            return;
        }

        setEditResumeLoading(true);

        try {
            // Get admin token first
            const adminToken = localStorage.getItem('token');
            if (!adminToken) {
                throw new Error('Admin token not found. Please log in again.');
            }

            // Get admin user's company ID from the candidate's company or fallback
            let companyId = editingCandidate.company?.[0]?.id;

            if (!companyId) {
                // Try to get from admin token if available (from login response)
                try {
                    // Decode JWT token to get company_id if it's stored there
                    const tokenPayload = JSON.parse(atob(adminToken.split('.')[1]));
                    companyId = tokenPayload.company_id;
                } catch (error) {
                    console.warn('Could not decode admin token for company_id');
                }
            }

            if (!companyId) {
                throw new Error('Company ID not found. Please ensure the candidate is associated with a company.');
            }

            const formData = new FormData();
            formData.append('company_id', companyId);

            // Add resume file if provided (use 'resume' key to match candidate update endpoint)
            if (editResumeUpload.file) {
                formData.append('resume', editResumeUpload.file);
            }

            // Let axios/browser set Content-Type with proper boundary. Only include Authorization header.
            const headers = {};
            if (adminToken) headers.Authorization = `Token ${adminToken}`;

            const response = await api.patch(`/candidates/${editingCandidate.id}/`, formData, { headers });
            if (response.data) {
                setSnackbar({
                    open: true,
                    message: 'Resume updated successfully!',
                    severity: 'success'
                });

                // Refresh candidates list
                fetchCandidates(page, pageSize, searchQuery, showArchived, jdTagFilter || null, selectedCompanyId, inviteStatusFilter || null, groupFilter || null);

                // Close dialog
                setOpenEditResumeDialog(false);
                setEditingCandidate(null);
                setEditResumeUpload({
                    isUploading: false,
                    file: null,
                    extractedData: null,
                    tempFileInfo: null,
                    parseError: null
                });
            }
        } catch (error) {
            console.error('Error updating resume:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to update resume. Please try again.',
                severity: 'error'
            });
        } finally {
            setEditResumeLoading(false);
        }
    };

    const StatusBadge = ({ status }) => {
        const colors = {
            Active: 'bg-green-100 text-green-800 border-green-200',
            Used: 'bg-gray-100 text-gray-800 border-gray-200',
            Expired: 'bg-red-100 text-red-800 border-red-200'
        };
        return (
            <span className={`px-1.5 py-0.5 rounded-full border text-xs font-medium ${colors[status] || colors.Used}`}>
                {status || '—'}
            </span>
        );
    };

    const ArchivedBadge = () => (
        <span className="px-1.5 py-0.5 rounded-full border bg-orange-100 text-orange-800 border-orange-200 flex items-center gap-1 text-xs">
            <IoArchiveOutline size={10} />
            Archived
        </span>
    );

    return (
        <PageShell className="bg-gray-50/50">
            <NotificationPopup
                open={snackbar.open}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
                    <PageCard overflow={false}>
                    <PageHeader
                        title={t('candidate.pageTitle')}
                        subtitle={t('candidate.pageSubtitle')}
                    >
                                <div className="relative inline-block" ref={addCandidateDropdownRef}>
                                    <PageHeaderButton
                                    onClick={() => setDropdownOpen(prev => !prev)}
                                    disabled={resumeUpload.isUploading || isMultiParsing}
                                    title="Add candidate or upload resumes"
                                    icon={
                                        isMultiParsing || resumeUpload.isUploading
                                            ? <Loader2 size={15} className="animate-spin" />
                                            : <Plus size={16} />
                                    }
                                >
                                    {isMultiParsing || resumeUpload.isUploading
                                        ? (isMultiParsing ? "Parsing..." : t('candidate.parsing'))
                                        : t('candidate.addCandidate', 'Add Candidate')}
                                    {!(isMultiParsing || resumeUpload.isUploading) && (
                                        <ChevronDown size={15} className={`ml-0.5 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                                    )}
                                    </PageHeaderButton>

                                {dropdownOpen && (
                                    <div className="absolute right-0 mt-1.5 w-60 bg-white border border-gray-100 rounded-xl shadow-xl z-50 py-1.5 animate-in fade-in slide-in-from-top-1 duration-150">
                                        <div
                                            onClick={() => {
                                                setDropdownOpen(false);
                                                setOpenAddDialog(true);
                                            }}
                                            className="px-3.5 py-2.5 text-sm text-gray-700 hover:bg-blue-50/70 hover:text-blue-700 cursor-pointer flex items-center gap-2.5 transition-colors border-b border-gray-100"
                                        >
                                            <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                                                <Plus size={15} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-medium text-xs sm:text-sm">{t('candidate.addCandidate', 'Add Candidate')}</div>
                                                <div className="text-[11px] text-gray-400">Manual entry form</div>
                                            </div>
                                        </div>
                                        <div
                                            onClick={() => {
                                                if (singleInputRef.current) singleInputRef.current.value = "";
                                                singleInputRef.current?.click();
                                                setDropdownOpen(false);
                                            }}
                                            className="px-3.5 py-2.5 text-sm text-gray-700 hover:bg-emerald-50/70 hover:text-emerald-700 cursor-pointer flex items-center gap-2.5 transition-colors border-b border-gray-100"
                                        >
                                            <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                                                <FileText size={15} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-medium text-xs sm:text-sm">{t('candidate.uploadSingle', 'Upload Single Resume')}</div>
                                                <div className="text-[11px] text-gray-400">PDF or DOCX document</div>
                                            </div>
                                        </div>
                                        <div
                                            onClick={() => {
                                                if (bulkInputRef.current) bulkInputRef.current.value = "";
                                                bulkInputRef.current?.click();
                                                setDropdownOpen(false);
                                            }}
                                            className="px-3.5 py-2.5 text-sm text-gray-700 hover:bg-purple-50/70 hover:text-purple-700 cursor-pointer flex items-center gap-2.5 transition-colors"
                                        >
                                            <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center text-purple-600 shrink-0">
                                                <Upload size={15} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-medium text-xs sm:text-sm">{t('candidate.uploadBulk', 'Upload Multiple Resumes')}</div>
                                                <div className="text-[11px] text-gray-400">ZIP archive of resumes</div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                    {/* Hidden Inputs */}
                                    <input
                                        ref={singleInputRef}
                                        type="file"
                                        accept=".pdf,.docx"
                                        className="hidden"
                                        onChange={handleUnifiedUpload}
                                        disabled={resumeUpload.isUploading || isMultiParsing}
                                        multiple
                                    />
                                    <input
                                        ref={bulkInputRef}
                                        type="file"
                                        accept=".zip"
                                        className="hidden"
                                        onChange={handleUnifiedUpload}
                                        disabled={resumeUpload.isUploading || isMultiParsing}
                                    />
                                </div>
                                {isModalOpen && (
                                    <BulkResumeUpload
                                        onClose={() => {
                                            setModalOpen(false);
                                            setIsMultiParsing(false);
                                            setBulkUploadFile(null);
                                            setBulkUploadFiles(null);
                                            setParsedBulkCandidates(null);
                                            setBulkUploadId(null);
                                            setBulkTotalFiles(0);
                                            fetchCandidates(page, pageSize, searchQuery, showArchived, jdTagFilter || null, selectedCompanyId, inviteStatusFilter || null, groupFilter || null);
                                        }}
                                        autoUploadFile={bulkUploadFile}
                                        autoUploadFiles={bulkUploadFiles}
                                        initialData={parsedBulkCandidates}
                                        initialUploadId={bulkUploadId}
                                        initialTotalFiles={bulkTotalFiles}
                                        candidateGroups={candidateGroups}
                                        selectedCompanyId={selectedCompanyId}
                                        companies={companies}
                                        onGroupCreated={(newGroup) => {
                                            setCandidateGroups((prev) => [newGroup, ...prev.filter((g) => g.id !== newGroup.id)]);
                                        }}
                                    />
                                )}
                    </PageHeader>
                    {/* Compact Filters */}
                    <div className="px-4 py-2 border-b bg-gray-50 shrink-0">
                        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center flex-wrap">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <Filter size={16} className="text-gray-400 flex-shrink-0" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder={t('candidate.searchPlaceholder')}
                                    className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="px-2.5 py-1.5 text-xs text-gray-600 hover:text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>

                            {/* Archived Filter */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={showArchived}
                                        onChange={(e) => setShowArchived(e.target.checked)}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-1"
                                    />
                                    <span className="text-sm text-gray-700">{t('candidate.showArchived')}</span>
                                </label>
                            </div>

                            {/* Company Filter (Super Admin Only) */}
                            {userRole === 'Super Admin' && (
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <select
                                        value={selectedCompanyId}
                                        onChange={(e) => {
                                            setSelectedCompanyId(e.target.value);
                                            setSearchParams({ page: '1' });
                                        }}
                                        className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white font-medium"
                                    >
                                        <option value="">{t('dashboard.allCompanies')}</option>
                                        <option value="b2c">B2C / Practice</option>
                                        {companies.map((company) => (
                                            <option key={company.id} value={company.id}>
                                                {company.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="relative group w-full sm:w-52 md:w-56" ref={filterDropdownRef}>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder={t('candidate.filterByJdTag')}
                                        value={jdFilterSearch}
                                        onChange={(e) => {
                                            setJdFilterSearch(e.target.value);
                                            setIsFilterDropdownOpen(true);
                                        }}
                                        onFocus={() => setIsFilterDropdownOpen(true)}
                                        className="w-full pl-3 pr-8 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all shadow-sm group-hover:border-gray-300"
                                    />
                                    {jdTagFilter && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setJdTagFilter('');
                                                setJdFilterSearch('');
                                                setPage(1);
                                                setSearchParams({ page: '1' });
                                                setIsFilterDropdownOpen(false);
                                            }}
                                            className="absolute right-8 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsFilterDropdownOpen(!isFilterDropdownOpen);
                                        }}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                                    >
                                        <ChevronDown size={14} className={`transition-transform duration-200 ${isFilterDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                </div>

                                {isFilterDropdownOpen && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[160px] overflow-y-auto overflow-x-hidden animate-in fade-in slide-in-from-top-1 duration-200">
                                        <div
                                            onClick={() => {
                                                setJdTagFilter('');
                                                setJdFilterSearch('');
                                                setPage(1);
                                                setSearchParams({ page: '1' });
                                                setIsFilterDropdownOpen(false);
                                            }}
                                            className="px-4 py-2 text-sm hover:bg-gray-50 cursor-pointer text-gray-500 font-medium border-b border-gray-100"
                                        >
                                            {t('candidate.allJdTags')}
                                        </div>
                                        {jobDescriptions
                                            .filter(jd => jd.title.toLowerCase().includes(jdFilterSearch.toLowerCase()))
                                            .map((jd) => (
                                                <div
                                                    key={jd.id}
                                                    onClick={() => {
                                                        setJdTagFilter(jd.id.toString());
                                                        setJdFilterSearch(jd.title);
                                                        setPage(1);
                                                        setSearchParams({ page: '1' });
                                                        setIsFilterDropdownOpen(false);
                                                    }}
                                                    className={`px-4 py-2.5 text-sm cursor-pointer truncate transition-colors hover:bg-blue-50 ${jdTagFilter === jd.id.toString() ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                                                    title={jd.title}
                                                >
                                                    {jd.title}
                                                </div>
                                            ))}
                                        {jobDescriptions.filter(jd => jd.title.toLowerCase().includes(jdFilterSearch.toLowerCase())).length === 0 && (
                                            <div className="px-4 py-6 text-center text-xs text-gray-400 italic">
                                                No matching Job Descriptions
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Invite Status Filter */}
                            <div className="relative group w-full sm:w-44 md:w-48" ref={inviteStatusDropdownRef}>
                                <div
                                    onClick={() => setIsInviteStatusDropdownOpen(!isInviteStatusDropdownOpen)}
                                    className="relative flex items-center justify-between w-full pl-3 pr-8 py-2 text-sm bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all shadow-sm select-none"
                                >
                                    <div className="flex items-center gap-1.5 truncate">
                                        {inviteStatusFilter ? (
                                            (() => {
                                                const opt = INVITE_STATUS_FILTER_OPTIONS.find(o => o.value === inviteStatusFilter);
                                                return opt ? (
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${opt.badgeClass}`}>
                                                        {opt.label}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-700 font-medium">{inviteStatusFilter}</span>
                                                );
                                            })()
                                        ) : (
                                            <span className="text-gray-400 truncate">
                                                {t('candidate.filterByInviteStatus', 'Filter by Invite Status')}
                                            </span>
                                        )}
                                    </div>
                                    {inviteStatusFilter && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setInviteStatusFilter('');
                                                setPage(1);
                                                setSearchParams({ page: '1' });
                                                setIsInviteStatusDropdownOpen(false);
                                            }}
                                            className="absolute right-8 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                                            title="Clear invite status filter"
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-gray-600 transition-colors pointer-events-none">
                                        <ChevronDown size={14} className={`transition-transform duration-200 ${isInviteStatusDropdownOpen ? 'rotate-180' : ''}`} />
                                    </div>
                                </div>

                                {isInviteStatusDropdownOpen && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200">
                                        <div
                                            onClick={() => {
                                                setInviteStatusFilter('');
                                                setPage(1);
                                                setSearchParams({ page: '1' });
                                                setIsInviteStatusDropdownOpen(false);
                                            }}
                                            className={`px-4 py-2 text-sm hover:bg-gray-50 cursor-pointer font-medium border-b border-gray-100 transition-colors ${!inviteStatusFilter ? 'text-blue-600 bg-blue-50/50' : 'text-gray-500'}`}
                                        >
                                            {t('candidate.allInviteStatuses', 'All Invite Statuses')}
                                        </div>
                                        {INVITE_STATUS_FILTER_OPTIONS.map((opt) => (
                                            <div
                                                key={opt.value}
                                                onClick={() => {
                                                    setInviteStatusFilter(opt.value);
                                                    setPage(1);
                                                    setSearchParams({ page: '1' });
                                                    setIsInviteStatusDropdownOpen(false);
                                                }}
                                                className={`px-4 py-2.5 text-sm cursor-pointer flex items-center justify-between transition-colors hover:bg-blue-50 ${inviteStatusFilter === opt.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                                            >
                                                <span>{opt.label}</span>
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${opt.badgeClass}`}>
                                                    {opt.label}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Group Filter */}
                            <div className="relative group w-full sm:w-44 md:w-48" ref={groupDropdownRef}>
                                <div
                                    onClick={() => setIsGroupDropdownOpen(!isGroupDropdownOpen)}
                                    className="relative flex items-center justify-between w-full pl-3 pr-8 py-2 text-sm bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all shadow-sm select-none"
                                >
                                    <div className="flex items-center gap-1.5 truncate">
                                        {groupFilter ? (
                                            (() => {
                                                const selectedGrp = candidateGroups.find(g => String(g.id) === String(groupFilter));
                                                return selectedGrp ? (
                                                    <span className="inline-flex items-center gap-1.5 text-gray-800 font-medium truncate text-xs sm:text-sm">
                                                        {selectedGrp.logo && <span>{selectedGrp.logo}</span>}
                                                        <span className="truncate">{selectedGrp.name}</span>
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-700 font-medium">{t('candidate.allGroups', 'All groups')}</span>
                                                );
                                            })()
                                        ) : (
                                            <span className="text-gray-400 truncate">
                                                {t('candidate.allGroups', 'All groups')}
                                            </span>
                                        )}
                                    </div>
                                    {groupFilter && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setGroupFilter('');
                                                setPage(1);
                                                setSearchParams({ page: '1' });
                                                setIsGroupDropdownOpen(false);
                                            }}
                                            className="absolute right-8 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                                            title="Clear group filter"
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-gray-600 transition-colors pointer-events-none">
                                        <ChevronDown size={14} className={`transition-transform duration-200 ${isGroupDropdownOpen ? 'rotate-180' : ''}`} />
                                    </div>
                                </div>

                                {isGroupDropdownOpen && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[220px] overflow-y-auto overflow-x-hidden animate-in fade-in slide-in-from-top-1 duration-200">
                                        <div
                                            onClick={() => {
                                                setGroupFilter('');
                                                setPage(1);
                                                setSearchParams({ page: '1' });
                                                setIsGroupDropdownOpen(false);
                                            }}
                                            className={`px-4 py-2 text-sm hover:bg-gray-50 cursor-pointer font-medium border-b border-gray-100 transition-colors ${!groupFilter ? 'text-blue-600 bg-blue-50/50' : 'text-gray-500'}`}
                                        >
                                            {t('candidate.allGroups', 'All groups')}
                                        </div>
                                        {candidateGroups.map((group) => (
                                            <div
                                                key={group.id}
                                                onClick={() => {
                                                    setGroupFilter(group.id.toString());
                                                    setPage(1);
                                                    setSearchParams({ page: '1' });
                                                    setIsGroupDropdownOpen(false);
                                                }}
                                                className={`px-4 py-2.5 text-sm cursor-pointer flex items-center gap-2 transition-colors hover:bg-blue-50 ${String(groupFilter) === String(group.id) ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                                                title={group.name}
                                            >
                                                {group.logo && <span className="text-base shrink-0">{group.logo}</span>}
                                                <span className="truncate">{group.name}</span>
                                            </div>
                                        ))}
                                        {candidateGroups.length === 0 && (
                                            <div className="px-4 py-4 text-center text-xs text-gray-400 italic">
                                                No groups available
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {selectedCandidateIds.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setAssignDialogOpen(true)}
                                    className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                                >
                                    Add to group ({selectedCandidateIds.length})
                                </button>
                            )}

                            {searchQuery && (
                                <div className="text-xs text-gray-600 bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-200 flex-shrink-0">
                                    Searching: <strong>{searchQuery}</strong>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Compact Content */}
                    <div className="flex-1 flex flex-col min-h-0 overflow-hidden p-3 sm:p-4">
                        {loading && candidates.length === 0 ? null : candidates.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <User size={40} className="mx-auto mb-3 opacity-50" />
                                <p className="text-base font-medium">{t('candidate.noCandidates')}</p>
                                <p className="text-sm">{t('candidate.addFirstCandidate')}</p>
                            </div>
                        ) : isMobile ? (
                            // Compact Mobile Cards
                            <div className="space-y-2 pr-1 flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                {sortedCandidates.map((candidate) => {
                                    return (
                                        <div key={candidate.id} className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-3 space-y-2 border border-gray-200 hover:shadow-sm transition-shadow">
                                            <div className="flex justify-between items-start">
                                                <input
                                                    type="checkbox"
                                                    className="mt-1 mr-2"
                                                    checked={selectedCandidateIds.includes(candidate.id)}
                                                    onChange={() => toggleCandidateSelection(candidate.id)}
                                                    aria-label={`Select ${candidate.firstName || ''} ${candidate.lastName || ''}`}
                                                />
                                                <div
                                                    className="cursor-pointer flex-1 min-w-0"
                                                    onClick={() => handleCandidateDetails(candidate)}
                                                >
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <h3 className="text-sm font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                                                            {highlightMatch(
                                                                `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim()
                                                            )}
                                                        </h3>
                                                        <LiveInterviewPing candidateId={candidate.id} liveInterviews={liveInterviews} />
                                                        {candidate.is_archived && <ArchivedBadge />}
                                                        <StatusBadge status={candidate.inviteStatus} />
                                                        <GroupLogos candidate={candidate} />
                                                    </div>
                                                    <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                                                        <Mail size={12} />
                                                        <span className="truncate">{highlightMatch(candidate.email)}</span>
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-1.5 text-xs">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">{t('candidate.created')}:</span>
                                                    <span className="text-gray-700 truncate ml-1">{formatDate(candidate.created_at)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">{t('candidate.inviteSent')}:</span>
                                                    <span className="text-gray-700 truncate ml-1">{candidate.invite_generated_at ? formatDate(candidate.invite_generated_at) : '—'}</span>
                                                </div>
                                            </div>

                                            <div className="flex justify-end items-center pt-1 border-t border-gray-100">
                                                <button
                                                    type="button"
                                                    onClick={(e) => handleOpenActionMenu(e, candidate)}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-xs transition-colors"
                                                    title={t('candidate.actions', 'Actions')}
                                                >
                                                    <MoreVertical size={14} className="text-gray-500" />
                                                    <span>{t('candidate.actions', 'Actions')}</span>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            // Compact Desktop Table
                            <div
                                ref={tableViewportRef}
                                className="bg-white rounded-lg shadow-xs border border-gray-200 flex-1 flex flex-col min-h-0 overflow-hidden"
                            >
                                <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
                                    <table className="w-full min-w-[940px] bg-white text-sm divide-y divide-gray-200 table-fixed">
                                        <thead className="bg-gray-50 sticky top-0 z-20">
                                            <tr>
                                                <th className={`py-2.5 px-3 w-12 min-w-[48px] max-w-[48px] sticky ${isRTL ? 'right-0' : 'left-0'} z-30 bg-gray-50 text-center`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={allVisibleSelected}
                                                        onChange={toggleVisibleSelection}
                                                        aria-label="Select all candidates on this page"
                                                    />
                                                </th>
                                                <th className={`text-left py-2.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider w-[220px] min-w-[200px] sticky ${isRTL ? 'right-[48px]' : 'left-[48px]'} z-30 bg-gray-50`}>
                                                    <SortButton column="firstName">{t('interviewOverview.candidate')}</SortButton>
                                                </th>
                                                <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider w-[220px] min-w-[200px]">
                                                    <SortButton column="email">{t('candidate.email')}</SortButton>
                                                </th>
                                                <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider w-[140px] min-w-[130px]">{t('candidate.phone')}</th>
                                                <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider w-[120px] min-w-[110px]">Invite Status</th>
                                                <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider w-[180px] min-w-[160px]" title={t('candidate.jobDescriptionsTooltip')}>{t('candidate.jdTag')}</th>
                                                <th className="text-center py-2.5 px-3 text-xs font-semibold text-gray-700 uppercase tracking-wider w-[64px] min-w-[64px]">{t('candidate.actions')}</th>
                                            </tr>
                                        </thead>

                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {sortedCandidates.map((candidate, index) => {
                                                return (
                                                    <tr key={candidate.id} className="bg-white hover:bg-blue-50/50 transition-colors duration-150 border-b border-gray-100 group">
                                                        <td className={`py-2.5 px-3 w-12 min-w-[48px] max-w-[48px] sticky ${isRTL ? 'right-0' : 'left-0'} z-10 bg-white group-hover:bg-blue-50/60 transition-colors text-center`}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedCandidateIds.includes(candidate.id)}
                                                                onChange={() => toggleCandidateSelection(candidate.id)}
                                                                aria-label={`Select ${candidate.firstName || ''} ${candidate.lastName || ''}`}
                                                            />
                                                        </td>
                                                        <td className={`py-2.5 px-4 w-[220px] min-w-[200px] sticky ${isRTL ? 'right-[48px]' : 'left-[48px]'} z-10 bg-white group-hover:bg-blue-50/60 transition-colors`}>
                                                            <div className="cursor-pointer" onClick={() => handleCandidateDetails(candidate)}>
                                                                <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                                                                    <div
                                                                        className="text-sm font-semibold text-gray-900 hover:text-blue-600 transition-colors truncate"
                                                                        title={`${candidate.firstName || ''} ${candidate.lastName || ''}`.trim()}
                                                                    >
                                                                        {highlightMatch(
                                                                            truncateName(candidate.firstName, candidate.lastName)
                                                                        )}
                                                                    </div>
                                                                    {userRole === 'Super Admin' && (
                                                                        candidate.companies && candidate.companies.length > 0 ? (
                                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200/50 uppercase tracking-wide">
                                                                                {candidate.companies[0].name}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-50 text-gray-600 border border-gray-200/60 uppercase tracking-wide">
                                                                                B2C / Practice
                                                                            </span>
                                                                        )
                                                                    )}
                                                                    <LiveInterviewPing candidateId={candidate.id} liveInterviews={liveInterviews} />
                                                                    {candidate.is_archived && <ArchivedBadge />}
                                                                    <GroupLogos candidate={candidate} />
                                                                </div>
                                                                <div className="text-xs text-gray-500 mt-0.5 font-normal">
                                                                    {formatJoinedSmall(
                                                                        candidate.dateJoined ||
                                                                        candidate.date_joined ||
                                                                        candidate.created_at ||
                                                                        ''
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="py-2.5 px-4 w-[220px] min-w-[200px]">
                                                            <div className="flex items-center justify-between gap-1.5 max-w-full">
                                                                <div
                                                                    className="text-sm text-gray-700 cursor-pointer hover:text-blue-600 transition-colors truncate font-normal min-w-0 flex-1"
                                                                    onClick={() => handleCandidateDetails(candidate)}
                                                                    title={candidate.email}
                                                                >
                                                                    {highlightMatch(candidate.email)}
                                                                </div>
                                                                {candidate.email && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigator.clipboard.writeText(candidate.email);
                                                                            setCopied(prev => ({ ...prev, email: { ...prev.email, [candidate.id]: true } }));
                                                                            setTimeout(() => {
                                                                                setCopied(prev => {
                                                                                    const next = { ...prev, email: { ...prev.email } };
                                                                                    delete next.email[candidate.id];
                                                                                    return next;
                                                                                });
                                                                            }, 1500);
                                                                        }}
                                                                        title={copied.email[candidate.id] ? 'Copied' : 'Copy email'}
                                                                        className={`p-1 rounded-md transition-all flex-shrink-0 ${copied.email[candidate.id] ? 'text-green-700 bg-green-100' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
                                                                    >
                                                                        {copied.email[candidate.id] ? <Check size={14} /> : <Copy size={14} />}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="py-2.5 px-4 w-[140px] min-w-[130px]">
                                                            <div className="flex items-center gap-2 text-sm text-gray-700 font-normal">
                                                                <span className="truncate">
                                                                    {highlightMatch(`${candidate.country_code || ''} ${candidate.mobile || ''}`.trim()) || <span className="text-gray-400">—</span>}
                                                                </span>
                                                                {(candidate.mobile || candidate.country_code) && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const value = `${candidate.country_code || ''} ${candidate.mobile || ''}`.trim();
                                                                            navigator.clipboard.writeText(value);
                                                                            setCopied(prev => ({ ...prev, phone: { ...prev.phone, [candidate.id]: true } }));
                                                                            setTimeout(() => {
                                                                                setCopied(prev => {
                                                                                    const next = { ...prev, phone: { ...prev.phone } };
                                                                                    delete next.phone[candidate.id];
                                                                                    return next;
                                                                                });
                                                                            }, 1500);
                                                                        }}
                                                                        title={copied.phone[candidate.id] ? 'Copied' : 'Copy phone'}
                                                                        className={`p-1 rounded-md transition-all flex-shrink-0 ${copied.phone[candidate.id] ? 'text-green-700 bg-green-100' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
                                                                    >
                                                                        {copied.phone[candidate.id] ? <Check size={14} /> : <Copy size={14} />}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="py-2.5 px-4 w-[120px] min-w-[110px]">
                                                            <div className="flex items-center">
                                                                <InviteStatusBadge status={candidate.inviteStatus} />
                                                            </div>
                                                        </td>
                                                        <td className="py-2.5 px-4 w-[180px] min-w-[160px]">
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {(candidate.jd_tags && candidate.jd_tags.length > 0)
                                                                    ? candidate.jd_tags.map((tag) => (
                                                                        <button
                                                                            key={tag.id}
                                                                            type="button"
                                                                            onClick={async (e) => {
                                                                                e.stopPropagation();
                                                                                setJdDescriptionLoading(true);
                                                                                try {
                                                                                    const res = await api.get(`job-descriptions/${tag.job_description_id}/`);
                                                                                    const desc = res?.data?.description ?? '';
                                                                                    setJdDescriptionModal({
                                                                                        open: true,
                                                                                        title: res?.data?.title ?? tag.title,
                                                                                        content: parse(convertMarkdownToHtml(desc)),
                                                                                    });
                                                                                } catch (err) {
                                                                                    setSnackbar({ open: true, message: err.response?.data?.detail || 'Failed to load description', severity: 'error' });
                                                                                } finally {
                                                                                    setJdDescriptionLoading(false);
                                                                                }
                                                                            }}
                                                                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 cursor-pointer max-w-[170px] shadow-2xs transition-colors"
                                                                            title={tag.title}
                                                                            disabled={jdDescriptionLoading}
                                                                        >
                                                                            <span className="truncate">{tag.title.length > 25 ? tag.title.slice(0, 25) + '…' : tag.title}</span>
                                                                        </button>
                                                                    ))
                                                                    : <span className="text-sm text-gray-400">—</span>
                                                                }
                                                            </div>
                                                        </td>
                                                        <td className="py-2.5 px-3 text-center w-[64px] min-w-[64px]">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => handleOpenActionMenu(e, candidate)}
                                                                className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer ${
                                                                    selectedActionCandidate?.id === candidate.id && Boolean(actionMenuAnchor)
                                                                        ? 'bg-blue-100 text-blue-700 shadow-xs'
                                                                        : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                                                                }`}
                                                                title={t('candidate.actions', 'Actions')}
                                                                aria-label="Actions"
                                                            >
                                                                <MoreVertical size={18} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="shrink-0 border-t border-gray-100 bg-white">
                                    <Pagination
                                        currentPage={page}
                                        totalPages={totalPages}
                                        totalCount={totalCandidates}
                                        currentCount={candidates.length}
                                        onPageChange={setPage}
                                        onPageSizeChange={setPageSizeMode}
                                        pageSize={pageSizeMode}
                                        pageSizeOptions={pageSizeOptions}
                                        showPageSize={true}
                                        forceVisible={true}
                                        hasActiveFilters={!!searchQuery || showArchived || !!jdTagFilter || !!inviteStatusFilter || !!groupFilter}
                                        fixed={false}
                                        className="py-0"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    </PageCard>

            {assignDialogOpen && (
                <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl text-[10.5px]" style={{ fontSize: '10.5px' }}>
                        <h2 className="text-[10.5px] font-semibold text-gray-900" style={{ fontSize: '10.5px' }}>Add to group</h2>
                        <p className="mt-1 text-[10.5px] text-gray-500" style={{ fontSize: '10.5px' }}>
                            {selectedCandidateIds.length} candidate(s) will be added. Existing memberships are left as they are.
                        </p>
                        <select
                            className="mt-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-[10.5px]"
                            style={{ fontSize: '10.5px' }}
                            value={assignGroupId}
                            onChange={(event) => setAssignGroupId(event.target.value)}
                        >
                            <option value="" style={{ fontSize: '10.5px' }}>Select a group</option>
                            {candidateGroups.map((group) => (
                                <option key={group.id} value={group.id} style={{ fontSize: '10.5px' }}>{group.logo ? `${group.logo} ${group.name}` : group.name}</option>
                            ))}
                        </select>
                        <div className="mt-4 flex justify-end gap-2">
                            <button type="button" onClick={() => setAssignDialogOpen(false)} className="rounded-lg px-4 py-2 text-[10.5px] text-gray-700 hover:bg-gray-100" style={{ fontSize: '10.5px' }}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={assigningGroup || !assignGroupId}
                                onClick={assignSelectedToGroup}
                                className="rounded-lg bg-blue-600 px-4 py-2 text-[10.5px] font-medium text-white disabled:opacity-60"
                                style={{ fontSize: '10.5px' }}
                            >
                                {assigningGroup ? 'Adding...' : 'Add'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Candidate Modal */}
            {
                openAddDialog && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[999999] overflow-y-auto animate-in fade-in duration-200" aria-modal="true" role="dialog">
                        <div className="bg-white rounded-2xl w-full max-w-[560px] my-4 shadow-2xl border border-gray-100 overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
                            {/* Header - Compact without big top/bottom gap */}
                            <div className="px-6 pt-3.5 pb-2 flex items-center justify-between">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                                        <UserPlus size={18} className="stroke-[2.2]" />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-bold text-gray-900 tracking-tight leading-tight">{t('candidate.addCandidateTitle', 'Add Candidate')}</h2>
                                        <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">{t('candidate.fillDetails', 'Fill in the candidate details')}</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleCloseAddDialog}
                                    className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                                    disabled={isAddingCandidate}
                                    aria-label="Close"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {/* Parse status alert if resume was parsed */}
                            {resumeUpload.extractedData && (
                                <div className="px-6 pb-2">
                                    {resumeUpload.parseError ? (
                                        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-xl">
                                            <FileX size={14} className="text-amber-500 flex-shrink-0" />
                                            <span>Resume uploaded but parsing failed - please fill manually</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-xl">
                                            <Check size={14} className="text-emerald-500 flex-shrink-0" />
                                            <span>Form pre-filled from: {resumeUpload.tempFileInfo?.original_name}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Form Body - Immediate transition, compact vertical rhythm */}
                            <form onSubmit={handleAddCandidate} className="flex flex-col flex-1 overflow-hidden">
                                <div className="px-6 pt-1 pb-4 overflow-y-auto max-h-[calc(85vh-130px)] space-y-4">
                                    {/* SECTION 1: BASIC DETAILS */}
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <span className="text-[11px] font-bold tracking-wider text-gray-400 uppercase">
                                                BASIC DETAILS
                                            </span>
                                            <div className="flex-1 h-[1px] bg-gray-100"></div>
                                        </div>

                                        {/* Row 1: First Name & Last Name */}
                                        <div className="grid grid-cols-2 gap-3.5">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                    First Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    name="firstName"
                                                    value={form.firstName}
                                                    onChange={handleInputChange}
                                                    className="w-full h-10 px-3.5 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 outline-none hover:border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                                                    placeholder="First name"
                                                    required
                                                    disabled={isAddingCandidate}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                    Last Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    name="lastName"
                                                    value={form.lastName}
                                                    onChange={handleInputChange}
                                                    className="w-full h-10 px-3.5 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 outline-none hover:border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                                                    placeholder="Last name"
                                                    required
                                                    disabled={isAddingCandidate}
                                                />
                                            </div>
                                        </div>

                                        {/* Row 2: Email Address & Mobile Number */}
                                        <div className="grid grid-cols-2 gap-3.5">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                    Email Address <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    name="email"
                                                    type="email"
                                                    value={form.email}
                                                    onChange={handleInputChange}
                                                    className="w-full h-10 px-3.5 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 outline-none hover:border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                                                    placeholder="candidate@example.com"
                                                    required
                                                    disabled={isAddingCandidate}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                    Mobile Number <span className="text-red-500">*</span>
                                                </label>
                                                <div
                                                    className="w-full"
                                                    style={{
                                                        '--react-international-phone-height': '40px',
                                                        '--react-international-phone-border-radius': '12px',
                                                        '--react-international-phone-font-size': '13px',
                                                        '--react-international-phone-border-color': '#e2e8f0',
                                                        '--react-international-phone-dropdown-item-font-size': '13px',
                                                        '--react-international-phone-selected-dropdown-item-background-color': '#eff6ff',
                                                    }}
                                                >
                                                    <PhoneInput
                                                        defaultCountry="in"
                                                        value={phoneE164}
                                                        onChange={(phone) => {
                                                            setPhoneE164(phone);
                                                            if (mobileError) setMobileError('');
                                                        }}
                                                        disabled={isAddingCandidate}
                                                        placeholder="98765 43210"
                                                        preferredCountries={['in', 'us', 'gb', 'cn', 'au', 'de', 'ae', 'sg', 'jp']}
                                                        inputProps={{
                                                            name: 'phone',
                                                            required: true,
                                                            placeholder: '98765 43210',
                                                            'aria-invalid': mobileError ? 'true' : 'false',
                                                        }}
                                                        className="w-full !flex"
                                                        inputClassName="!flex-1 !h-10 !text-xs sm:!text-sm !text-gray-800 placeholder:!text-gray-400 !border-gray-200 !rounded-r-xl focus:!border-blue-500 focus:!ring-2 focus:!ring-blue-100 !outline-none !px-3"
                                                        countrySelectorStyleProps={{
                                                            buttonClassName: '!h-10 !px-2.5 !rounded-l-xl !border-gray-200 hover:!bg-gray-50 !bg-white !text-xs sm:!text-sm !text-gray-700 !flex !items-center !gap-1.5 focus:!border-blue-500',
                                                            dropdownStyleProps: {
                                                                className: '!z-[10000001] !shadow-xl !rounded-xl !border-gray-200',
                                                            },
                                                        }}
                                                    />
                                                </div>
                                                {mobileError && <p className="text-xs text-red-500 mt-1">{mobileError}</p>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* SECTION 2: JOB AND GROUP */}
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <span className="text-[11px] font-bold tracking-wider text-gray-400 uppercase">
                                                JOB AND GROUP
                                            </span>
                                            <div className="flex-1 h-[1px] bg-gray-100"></div>
                                        </div>

                                        {/* Job Description Field */}
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="flex items-center gap-1.5">
                                                    <label className="text-xs font-semibold text-gray-700">
                                                        Job Description
                                                    </label>
                                                    <span title={t('candidate.jobDescriptionsTooltip', 'Assign candidate to a job description')} className="text-gray-400 hover:text-gray-600 cursor-help">
                                                        <Info size={13} />
                                                    </span>
                                                </div>
                                                <span className="text-[10px] font-semibold text-gray-400 tracking-wider bg-gray-100 px-2 py-0.5 rounded uppercase">
                                                    OPTIONAL
                                                </span>
                                            </div>

                                            <div className="relative" ref={addJdDropdownRef}>
                                                <div
                                                    onClick={() => !isAddingCandidate && setIsAddJdDropdownOpen(!isAddJdDropdownOpen)}
                                                    className={`w-full h-10 px-3.5 flex items-center justify-between bg-white border ${
                                                        isAddJdDropdownOpen ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'
                                                    } rounded-xl text-xs sm:text-sm cursor-pointer transition-all`}
                                                >
                                                    <span className={addCandidateJdSearch ? 'text-gray-800 font-medium truncate' : 'text-gray-400'}>
                                                        {addCandidateJdSearch || 'Select job description'}
                                                    </span>
                                                    <div className="flex items-center gap-1 text-gray-400">
                                                        {addCandidateJdTagId && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setAddCandidateJdTagId('');
                                                                    setAddCandidateJdSearch('');
                                                                }}
                                                                className="p-1 hover:bg-gray-100 rounded-full hover:text-gray-600 mr-1"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        )}
                                                        <ChevronDown size={16} className={`transition-transform duration-200 ${isAddJdDropdownOpen ? 'rotate-180' : ''}`} />
                                                    </div>
                                                </div>

                                                {isAddJdDropdownOpen && (
                                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] p-2 animate-in fade-in slide-in-from-top-1 duration-150">
                                                        <input
                                                            type="text"
                                                            autoFocus
                                                            placeholder="Search job description..."
                                                            value={addCandidateJdSearch}
                                                            onChange={(e) => setAddCandidateJdSearch(e.target.value)}
                                                            className="w-full px-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg outline-none focus:bg-white focus:border-blue-500 mb-2"
                                                        />
                                                        <div className="max-h-44 overflow-y-auto space-y-1">
                                                            <div
                                                                onClick={() => {
                                                                    setAddCandidateJdTagId('');
                                                                    setAddCandidateJdSearch('');
                                                                    setIsAddJdDropdownOpen(false);
                                                                }}
                                                                className="px-3 py-1.5 text-xs text-gray-400 italic hover:bg-gray-50 rounded-lg cursor-pointer"
                                                            >
                                                                None (do not assign a job description)
                                                            </div>
                                                            {jobDescriptions
                                                                .filter(jd => (jd.title || '').toLowerCase().includes((addCandidateJdSearch || '').toLowerCase()))
                                                                .map((jd) => (
                                                                    <div
                                                                        key={jd.id}
                                                                        onClick={() => {
                                                                            setAddCandidateJdTagId(String(jd.id));
                                                                            setAddCandidateJdSearch(jd.title);
                                                                            setIsAddJdDropdownOpen(false);
                                                                        }}
                                                                        className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer transition-colors flex items-center justify-between ${
                                                                            addCandidateJdTagId === String(jd.id)
                                                                                ? 'bg-blue-50 text-blue-700 font-semibold'
                                                                                : 'text-gray-700 hover:bg-gray-50'
                                                                        }`}
                                                                    >
                                                                        <span className="truncate">{jd.title}</span>
                                                                        {addCandidateJdTagId === String(jd.id) && <Check size={14} className="text-blue-600 flex-shrink-0" />}
                                                                    </div>
                                                                ))}
                                                            {jobDescriptions.filter(jd => (jd.title || '').toLowerCase().includes((addCandidateJdSearch || '').toLowerCase())).length === 0 && (
                                                                <div className="px-3 py-3 text-center text-xs text-gray-400 italic">
                                                                    No matching Job Descriptions
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Candidate Group Field */}
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="flex items-center gap-1.5">
                                                    <label className="text-xs font-semibold text-gray-700">
                                                        Candidate Group
                                                    </label>
                                                    <span title="Optional group to organize candidates" className="text-gray-400 hover:text-gray-600 cursor-help">
                                                        <Info size={13} />
                                                    </span>
                                                </div>
                                                <span className="text-[10px] font-semibold text-gray-400 tracking-wider bg-gray-100 px-2 py-0.5 rounded uppercase">
                                                    OPTIONAL
                                                </span>
                                            </div>

                                            {/* Toggle Pills: Select existing vs Create new */}
                                            <div className="grid grid-cols-2 p-1 bg-gray-100/70 border border-gray-200/60 rounded-xl gap-1 mb-2">
                                                <button
                                                    type="button"
                                                    disabled={isAddingCandidate}
                                                    onClick={() => setAddCandidateGroupMode('select')}
                                                    className={`py-1.5 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
                                                        addCandidateGroupMode === 'select'
                                                            ? 'bg-white text-blue-600 border border-gray-200/80 shadow-xs'
                                                            : 'text-gray-500 hover:text-gray-800'
                                                    }`}
                                                >
                                                    <Folder size={14} className={addCandidateGroupMode === 'select' ? 'text-blue-600' : 'text-gray-400'} />
                                                    <span>Select existing</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={isAddingCandidate}
                                                    onClick={() => setAddCandidateGroupMode('create')}
                                                    className={`py-1.5 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
                                                        addCandidateGroupMode === 'create'
                                                            ? 'bg-white text-blue-600 border border-gray-200/80 shadow-xs'
                                                            : 'text-gray-500 hover:text-gray-800'
                                                    }`}
                                                >
                                                    <Plus size={14} className={addCandidateGroupMode === 'create' ? 'text-blue-600' : 'text-gray-400'} />
                                                    <span>Create new</span>
                                                </button>
                                            </div>

                                            {/* Mode 1: Select existing - Polished custom dropdown (not raw HTML select) */}
                                            {addCandidateGroupMode === 'select' && (
                                                <div className="relative" ref={addGroupDropdownRef}>
                                                    <div
                                                        onClick={() => !isAddingCandidate && setIsAddGroupDropdownOpen(!isAddGroupDropdownOpen)}
                                                        className={`w-full h-10 px-3.5 flex items-center justify-between bg-white border ${
                                                            isAddGroupDropdownOpen ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'
                                                        } rounded-xl text-xs sm:text-sm cursor-pointer transition-all`}
                                                    >
                                                        <span className="truncate text-gray-700 flex items-center gap-2">
                                                            {(() => {
                                                                const selGroup = candidateGroups.find(g => String(g.id) === String(addCandidateGroupId));
                                                                if (!selGroup) return <span className="text-gray-600">None (do not assign to a group)</span>;
                                                                return (
                                                                    <>
                                                                        <span>{selGroup.logo || '👥'}</span>
                                                                        <span className="font-medium text-gray-800">{selGroup.name}</span>
                                                                        {selGroup.member_count !== undefined && (
                                                                            <span className="text-xs text-gray-400">({selGroup.member_count})</span>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </span>
                                                        <div className="flex items-center gap-1 text-gray-400">
                                                            {addCandidateGroupId && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setAddCandidateGroupId('');
                                                                    }}
                                                                    className="p-1 hover:bg-gray-100 rounded-full hover:text-gray-600 mr-1"
                                                                    title="Clear group selection"
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                            )}
                                                            <ChevronDown size={16} className={`transition-transform duration-200 ${isAddGroupDropdownOpen ? 'rotate-180' : ''}`} />
                                                        </div>
                                                    </div>

                                                    {isAddGroupDropdownOpen && (
                                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] p-2 animate-in fade-in slide-in-from-top-1 duration-150">
                                                            {candidateGroups.length > 5 && (
                                                                <input
                                                                    type="text"
                                                                    autoFocus
                                                                    placeholder="Search candidate group..."
                                                                    value={addGroupSearch}
                                                                    onChange={(e) => setAddGroupSearch(e.target.value)}
                                                                    className="w-full px-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg outline-none focus:bg-white focus:border-blue-500 mb-2"
                                                                />
                                                            )}
                                                            <div className="max-h-44 overflow-y-auto space-y-1">
                                                                <div
                                                                    onClick={() => {
                                                                        setAddCandidateGroupId('');
                                                                        setIsAddGroupDropdownOpen(false);
                                                                    }}
                                                                    className={`px-3 py-2 text-xs rounded-lg cursor-pointer transition-colors flex items-center justify-between ${
                                                                        !addCandidateGroupId
                                                                            ? 'bg-blue-50 text-blue-700 font-semibold'
                                                                            : 'text-gray-600 hover:bg-gray-50'
                                                                    }`}
                                                                >
                                                                    <span>None (do not assign to a group)</span>
                                                                    {!addCandidateGroupId && <Check size={14} className="text-blue-600 flex-shrink-0" />}
                                                                </div>
                                                                {candidateGroups
                                                                    .filter(g => (g.name || '').toLowerCase().includes((addGroupSearch || '').toLowerCase()))
                                                                    .map((group) => (
                                                                        <div
                                                                            key={group.id}
                                                                            onClick={() => {
                                                                                setAddCandidateGroupId(String(group.id));
                                                                                setIsAddGroupDropdownOpen(false);
                                                                            }}
                                                                            className={`px-3 py-2 text-xs rounded-lg cursor-pointer transition-colors flex items-center justify-between ${
                                                                                addCandidateGroupId === String(group.id)
                                                                                    ? 'bg-blue-50 text-blue-700 font-semibold'
                                                                                    : 'text-gray-700 hover:bg-gray-50'
                                                                            }`}
                                                                        >
                                                                            <span className="truncate flex items-center gap-1.5">
                                                                                <span>{group.logo || '👥'}</span>
                                                                                <span>{group.name}</span>
                                                                                {group.member_count !== undefined && (
                                                                                    <span className="text-[11px] text-gray-400">({group.member_count})</span>
                                                                                )}
                                                                            </span>
                                                                            {addCandidateGroupId === String(group.id) && (
                                                                                <Check size={14} className="text-blue-600 flex-shrink-0" />
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                {candidateGroups.filter(g => (g.name || '').toLowerCase().includes((addGroupSearch || '').toLowerCase())).length === 0 && candidateGroups.length > 0 && (
                                                                    <div className="px-3 py-3 text-center text-xs text-gray-400 italic">
                                                                        No matching candidate groups
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Mode 2: Create new */}
                                            {addCandidateGroupMode === 'create' && (
                                                <div className="p-3 bg-blue-50/40 border border-blue-200/70 rounded-xl space-y-2.5 animate-in fade-in duration-150">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs font-semibold text-blue-900 flex items-center gap-1.5">
                                                            <Sparkles size={13} className="text-blue-600" />
                                                            New Candidate Group
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => setAddCandidateGroupMode('select')}
                                                            className="text-xs text-gray-400 hover:text-gray-600"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                    <div>
                                                        <label className="block text-[11px] font-medium text-gray-600 mb-1">
                                                            Group Name <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={newCandidateGroup.name}
                                                            onChange={(e) => setNewCandidateGroup(prev => ({ ...prev, name: e.target.value }))}
                                                            placeholder="e.g. Frontend Engineers"
                                                            className="w-full h-9 px-3 text-xs bg-white border border-gray-200 rounded-lg outline-none focus:border-blue-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[11px] font-medium text-gray-600 mb-1">
                                                            Group Icon
                                                        </label>
                                                        <div className="flex gap-1.5">
                                                            {['🌟', '🚀', '💻', '🎯', '👥', '⚡'].map((emoji) => (
                                                                <button
                                                                    key={emoji}
                                                                    type="button"
                                                                    onClick={() => setNewCandidateGroup(prev => ({ ...prev, logo: emoji }))}
                                                                    className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm border transition-all ${
                                                                        newCandidateGroup.logo === emoji
                                                                            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100 scale-105'
                                                                            : 'border-gray-200 bg-white hover:border-gray-300'
                                                                    }`}
                                                                >
                                                                    {emoji}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* SECTION 3: RESUME */}
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <span className="text-[11px] font-bold tracking-wider text-gray-400 uppercase">
                                                RESUME
                                            </span>
                                            <div className="flex-1 h-[1px] bg-gray-100"></div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                Resume <span className="text-red-500">*</span>
                                            </label>

                                            {/* Dropzone - sleek, compact */}
                                            <div
                                                onClick={() => !isAddingCandidate && resumeFileInputRef.current?.click()}
                                                onDragOver={(e) => {
                                                    e.preventDefault();
                                                    setIsDraggingResume(true);
                                                }}
                                                onDragLeave={() => setIsDraggingResume(false)}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    setIsDraggingResume(false);
                                                    const file = e.dataTransfer.files?.[0];
                                                    if (file) handleResumeFileSelect(file);
                                                }}
                                                className={`w-full border-2 border-dashed ${
                                                    isDraggingResume
                                                        ? 'border-blue-500 bg-blue-50/60 ring-2 ring-blue-100'
                                                        : 'border-blue-200 hover:border-blue-300 bg-blue-50/20 hover:bg-blue-50/40'
                                                } rounded-xl p-3.5 py-4 flex items-center gap-3.5 cursor-pointer transition-all group`}
                                            >
                                                <div className="w-9 h-9 rounded-xl bg-white border border-blue-100 shadow-xs flex items-center justify-center text-blue-600 flex-shrink-0 group-hover:scale-105 transition-transform">
                                                    <Upload size={17} className="stroke-[2.2]" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs text-gray-700">
                                                        Drag a file here or <span className="text-blue-600 font-semibold underline-offset-2 hover:underline">choose file</span>
                                                    </p>
                                                    <p className="text-[11px] text-gray-400 mt-0.5">
                                                        PDF, DOC or DOCX, up to 10 MB
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Hidden file input */}
                                            <input
                                                ref={resumeFileInputRef}
                                                type="file"
                                                accept=".pdf,.doc,.docx"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) handleResumeFileSelect(file);
                                                }}
                                                className="hidden"
                                                disabled={isAddingCandidate}
                                            />

                                            {/* Attached file display */}
                                            {(form.resume || resumeUpload.tempFileInfo) && (
                                                <div className="mt-2.5 flex items-center justify-between p-2.5 bg-blue-50/70 border border-blue-200/80 rounded-xl text-xs text-blue-900 animate-in fade-in">
                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                        <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                                                            <FileText size={15} />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="font-semibold text-gray-900 truncate text-xs">
                                                                {form.resume ? form.resume.name : resumeUpload.tempFileInfo?.original_name}
                                                            </p>
                                                            <p className="text-[11px] text-gray-500">
                                                                {form.resume
                                                                    ? `${(form.resume.size / 1024 / 1024).toFixed(2)} MB`
                                                                    : `${(resumeUpload.tempFileInfo.size / 1024 / 1024).toFixed(2)} MB (from upload)`}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setForm(prev => ({ ...prev, resume: null }));
                                                            if (resumeFileInputRef.current) resumeFileInputRef.current.value = '';
                                                        }}
                                                        className="p-1 hover:bg-blue-100 rounded-lg text-gray-400 hover:text-red-500 transition-colors"
                                                        title="Remove resume"
                                                    >
                                                        <X size={15} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Modal Footer */}
                                <div className="px-6 py-3.5 bg-white border-t border-gray-100 flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={handleCloseAddDialog}
                                        disabled={isAddingCandidate}
                                        className="flex-1 h-10 border border-gray-200 rounded-xl text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isAddingCandidate}
                                        className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm shadow-sm hover:shadow transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                    >
                                        {isAddingCandidate ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                <span>Adding...</span>
                                            </>
                                        ) : (
                                            <span>Add Candidate</span>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* Delete Confirmation Modal */}
            {
                dialogOpen && (
                    <div className="fixed top-16 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl text-[10.5px]" style={{ fontSize: '10.5px' }}>
                            <div className="p-6">
                                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                                    <IoArchiveOutline className="text-red-600" size={24} />
                                </div>
                                <h3 className="text-gray-900 mb-2 text-center text-[10.5px] font-semibold" style={{ fontSize: '10.5px' }}>{t('candidate.archiveConfirm')}</h3>
                                <p className="text-gray-600 mb-6 text-center text-[10.5px]" style={{ fontSize: '10.5px' }}>
                                    {t('candidate.archiveMessage')}
                                </p>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setDialogOpen(false)}
                                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-[10.5px]"
                                        style={{ fontSize: '10.5px' }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDeleteCandidate}
                                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-[10.5px]"
                                        style={{ fontSize: '10.5px' }}
                                    >
                                        {t('candidate.archive')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Expire Invite Confirmation Modal */}
            {
                expireDialogOpen && (
                    <div className="fixed top-16 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl text-[10.5px]" style={{ fontSize: '10.5px' }}>
                            <div className="p-6">
                                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-orange-100 rounded-full">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M18 6L6 18M6 6l12 12" stroke="#f97316" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </div>
                                <h3 className="text-gray-900 mb-2 text-center text-[10.5px] font-semibold" style={{ fontSize: '10.5px' }}>Expire Invite</h3>
                                <p className="text-gray-600 mb-6 text-center text-[10.5px]" style={{ fontSize: '10.5px' }}>
                                    Do you want to expire this interview invite? This action cannot be undone and the candidate will no longer be able to access the interview.
                                </p>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => {
                                            setExpireDialogOpen(false);
                                            setExpireToken(null);
                                        }}
                                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-[10.5px]"
                                        style={{ fontSize: '10.5px' }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleConfirmExpireInvite}
                                        className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-[10.5px]"
                                        style={{ fontSize: '10.5px' }}
                                    >
                                        Expire
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Generated Invite Success Modal */}
            {
                generatedInvite && (
                    <GenerateInviteSuccessfully
                        open={!!generatedInvite}
                        onClose={() => setGeneratedInvite(null)}
                        generatedInvite={generatedInvite}
                        copiedInviteUrl={copiedInviteUrl}
                        copyToClipboard={copyToClipboard}
                    />
                )
            }

            {/* Feedback Modal */}
            {
                showFeedbackModal && (
                    <ViewResult
                        onClose={() => setShowFeedbackModal(false)}
                        isSendingEmail={isSendingEmail}
                        emailSent={emailSent}
                        sendEmail={sendEmail}
                    />
                )
            }

            {/* Email Confirmation Modal */}
            {
                showEmailConfirmModal && (
                    <div className="fixed top-16 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl text-[10.5px]" style={{ fontSize: '10.5px' }}>
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-gray-900 text-[10.5px] font-semibold" style={{ fontSize: '10.5px' }}>Send Interview Feedback</h3>
                                    <button
                                        onClick={() => {
                                            setShowEmailConfirmModal(false);
                                            setCcRecipients('');
                                        }}
                                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4 mb-4 text-[10.5px]" style={{ fontSize: '10.5px' }}>
                                    <div className="mb-3">
                                        <div className="text-gray-600 mb-1 text-[10.5px]" style={{ fontSize: '10.5px' }}>Recipient Name</div>
                                        <div className="text-gray-900 font-medium text-[10.5px]" style={{ fontSize: '10.5px' }}>{emailRecipient?.name}</div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="text-gray-600 mb-1 text-[10.5px]" style={{ fontSize: '10.5px' }}>Email Address</div>
                                        <div className="text-gray-900 font-medium text-[10.5px]" style={{ fontSize: '10.5px' }}>{emailRecipient?.email}</div>
                                    </div>
                                    <div>
                                        <div className="text-gray-600 mb-1 text-[10.5px]" style={{ fontSize: '10.5px' }}>CC Recipients</div>
                                        <input
                                            type="text"
                                            value={ccRecipients}
                                            onChange={(e) => setCcRecipients(e.target.value)}
                                            placeholder="Enter email addresses (comma-separated)"
                                            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-[10.5px]"
                                            style={{ fontSize: '10.5px' }}
                                        />
                                        <p className="text-gray-500 mt-1 text-[10.5px]" style={{ fontSize: '10.5px' }}>Separate multiple email addresses with commas</p>
                                    </div>
                                </div>

                                <div className="bg-blue-50 rounded-lg p-3 mb-6 text-[10.5px]" style={{ fontSize: '10.5px' }}>
                                    <div className="text-blue-900 mb-2 font-medium text-[10.5px]" style={{ fontSize: '10.5px' }}>Email will include:</div>
                                    <ul className="text-blue-800 space-y-1 text-[10.5px]" style={{ fontSize: '10.5px' }}>
                                        <li>• Interview feedback summary</li>
                                        <li>• Overall score and category scores</li>
                                        <li>• Question & answer pairs with scores</li>
                                        {emailRecipient?.htmlContent ? (
                                            <li className="text-green-700">• ✅ PDF report attachment</li>
                                        ) : (
                                            <li className="text-amber-700">• ⚠️ PDF report (generation failed)</li>
                                        )}
                                    </ul>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => {
                                            setShowEmailConfirmModal(false);
                                            setCcRecipients('');
                                        }}
                                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-[10.5px]"
                                        style={{ fontSize: '10.5px' }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleConfirmEmail}
                                        disabled={isSendingEmail}
                                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-[10.5px] font-medium"
                                        style={{ fontSize: '10.5px' }}
                                    >
                                        {isSendingEmail ? (
                                            <>
                                                <Loader2 className="animate-spin" size={14} />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <Send size={14} />
                                                Send Email
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Invite Generation Modal */}
            <GenerateInviteModal
                isOpen={showInviteModal}
                onClose={() => {
                    setShowInviteModal(false);
                    setSelectedCandidateForInvite(null);
                }}
                selectedCandidate={selectedCandidateForInvite}
                onGenerateInvite={handleConfirmInvite}
                onGenerateInviteWithSkills={handleGenerateInviteWithSkills}
                isGeneratingInvite={isGeneratingInvite}
                setSnackbar={setSnackbar}
            />

            {/* Multi-JD Scheduling Modal */}
            <MultiJDScheduleModal
                isOpen={showMultiJDModal}
                onClose={() => {
                    setShowMultiJDModal(false);
                    setSelectedSkills({});
                    setSelectedJDs([]);
                    setMultiJDResults(null);
                    setSelectedCandidateForMultiJD(null);
                }}
                selectedCandidate={selectedCandidateForMultiJD}
                jobDescriptions={jobDescriptions}
                onCreateInvites={handleCreateMultiJDInvites}
                onCreateInvitesWithSkills={handleCreateMultiJDInvitesWithSkills}
                onSkillAnalysis={handleSkillAnalysis}
                selectedJDs={selectedJDs}
                setSelectedJDs={setSelectedJDs}
                selectedSkills={selectedSkills}
                setSelectedSkills={setSelectedSkills}
                multiJDResults={multiJDResults}
                isCreatingInvites={isCreatingMultiJDInvites}
                setSnackbar={setSnackbar}
            />

            {/* Skill Analysis Modal */}
            <SkillAnalysisModal
                isOpen={showSkillAnalysisModal}
                onClose={handleApplySkillsAndReturn}
                selectedCandidate={selectedCandidateForMultiJD}
                selectedJDForAnalysis={selectedJDForAnalysis}
                skillAnalysis={skillAnalysis}
                skillAnalysisLoading={skillAnalysisLoading}
                selectedSkills={selectedSkills}
                setSelectedSkills={setSelectedSkills}
                onApplySkillsAndReturn={handleApplySkillsAndReturn}
                reanalyzeSkills={reanalyzeSkills}
                onClearCachesAndRestart={handleClearCachesAndRestart}
            />

            {/* Comments Modal */}
            <CommentsModal
                isOpen={showCommentsModal}
                onClose={() => {
                    setShowCommentsModal(false);
                    setSelectedCandidateForComments(null);
                }}
                module="candidate"
                objectId={selectedCandidateForComments?.id}
                title="Candidate Comments"
                moduleName="Candidate"
                objectName={selectedCandidateForComments ? `${selectedCandidateForComments.firstName || ''} ${selectedCandidateForComments.lastName || ''}`.trim() : null}
                objectDetails={selectedCandidateForComments}
            />

            {/* Edit Resume Modal */}
            {
                openEditResumeDialog && (
                    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/10 flex items-center justify-center p-4 z-[999999]" aria-modal="true" role="dialog">
                        <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl text-[10.5px]" style={{ fontSize: '10.5px' }}>
                            <div className="p-4">
                                <div className="flex justify-between items-center mb-4">
                                    <div>
                                        <h2 className="text-gray-900 text-[10.5px] font-semibold" style={{ fontSize: '10.5px' }}>Update Resume</h2>
                                        <p className="text-gray-600 mt-1 text-[10.5px]" style={{ fontSize: '10.5px' }}>Upload new resume for {editingCandidate?.firstName} {editingCandidate?.lastName}</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setOpenEditResumeDialog(false);
                                            setEditingCandidate(null);
                                            setEditResumeUpload({
                                                isUploading: false,
                                                file: null,
                                                extractedData: null,
                                                tempFileInfo: null,
                                                parseError: null
                                            });
                                            // Clean up global reference
                                            if (window.editResumeFileInput) {
                                                delete window.editResumeFileInput;
                                            }
                                        }}
                                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={editResumeLoading || editResumeUpload.isUploading}
                                    >
                                        <X size={16} />
                                    </button>
                                </div>

                                <form onSubmit={handleUpdateResume} className="space-y-3">
                                    <div>
                                        <label className="block text-gray-700 mb-1.5 text-[10.5px] font-medium" style={{ fontSize: '10.5px' }}>
                                            Resume File *
                                        </label>
                                        <div className="relative">
                                            <input
                                                ref={(input) => { window.editResumeFileInput = input; }}
                                                type="file"
                                                accept=".pdf,.docx"
                                                onChange={handleEditResumeUpload}
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                disabled={editResumeUpload.isUploading}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => window.editResumeFileInput?.click()}
                                                className="w-full flex items-center gap-2 bg-gray-50 text-gray-700 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-[10.5px]"
                                                style={{ fontSize: '10.5px' }}
                                                disabled={editResumeUpload.isUploading}
                                            >
                                                {editResumeUpload.isUploading ? (
                                                    <Loader2 size={14} className="animate-spin" />
                                                ) : (
                                                    <Upload size={14} />
                                                )}
                                                {editResumeUpload.isUploading ? 'Processing...' : 'Choose Resume File'}
                                            </button>
                                        </div>
                                        {editResumeUpload.file ? (
                                            <p className="text-green-600 mt-1 text-[10.5px]" style={{ fontSize: '10.5px' }}>
                                                ✓ {editResumeUpload.file.name} selected
                                            </p>
                                        ) : (
                                            <p className="text-gray-500 mt-1 text-[10.5px]" style={{ fontSize: '10.5px' }}>
                                                Please select a resume file (PDF or DOCX)
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex gap-2.5 pt-3">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setOpenEditResumeDialog(false);
                                                setEditingCandidate(null);
                                                setEditResumeUpload({
                                                    isUploading: false,
                                                    file: null,
                                                    extractedData: null,
                                                    tempFileInfo: null,
                                                    parseError: null
                                                });
                                                // Clean up global reference
                                                if (window.editResumeFileInput) {
                                                    delete window.editResumeFileInput;
                                                }
                                            }}
                                            className="flex-1 px-4 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-[10.5px]"
                                            style={{ fontSize: '10.5px' }}
                                            disabled={editResumeLoading || editResumeUpload.isUploading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={editResumeLoading || !editResumeUpload.file}
                                            className="flex-1 px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-[10.5px] font-medium"
                                            style={{ fontSize: '10.5px' }}
                                        >
                                            {editResumeLoading ? (
                                                <>
                                                    <Loader2 className="animate-spin" size={14} />
                                                    Updating...
                                                </>
                                            ) : (
                                                <>
                                                    <Upload size={14} />
                                                    Update Resume
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Candidate Actions Menu (3-Dots) */}
            <Menu
                id="candidate-actions-menu"
                anchorEl={actionMenuAnchor}
                open={Boolean(actionMenuAnchor)}
                onClose={handleCloseActionMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: isRTL ? 'left' : 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: isRTL ? 'left' : 'right',
                }}
                slotProps={{
                    paper: {
                        elevation: 3,
                        sx: {
                            mt: 0.5,
                            borderRadius: '12px',
                            minWidth: '205px',
                            border: '1px solid #f1f5f9',
                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
                            py: 0.5,
                            '& .MuiMenuItem-root': {
                                px: 1.5,
                                py: 1,
                                fontSize: '0.8125rem',
                                borderRadius: '8px',
                                mx: 0.5,
                                my: 0.25,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                color: '#334155',
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                    backgroundColor: '#f8fafc',
                                    color: '#0f172a',
                                },
                            },
                        },
                    },
                }}
            >
                {/* 1. View Details */}
                <MenuItem onClick={() => handleMenuAction(handleCandidateDetails, selectedActionCandidate)}>
                    <Eye size={15} className="text-blue-600 shrink-0" />
                    <span className="font-medium">{t('candidate.viewDetails', 'View Details')}</span>
                </MenuItem>

                {/* 2. View Resume (if exists) */}
                {selectedActionCandidate?.resume_url && (
                    <MenuItem
                        onClick={() => {
                            const url = selectedActionCandidate.resume_url;
                            handleCloseActionMenu();
                            window.open(url, '_blank', 'noopener,noreferrer');
                        }}
                    >
                        <FileText size={15} className="text-purple-600 shrink-0" />
                        <span className="font-medium">{t('candidate.viewResume', 'View Resume')}</span>
                    </MenuItem>
                )}

                {/* 3. Update Resume */}
                <MenuItem onClick={() => handleMenuAction(handleEditResume, selectedActionCandidate)}>
                    <Upload size={15} className="text-indigo-600 shrink-0" />
                    <span className="font-medium">{t('candidate.updateResume', 'Update Resume')}</span>
                </MenuItem>

                {/* 4. Multi-JD Schedule */}
                <MenuItem onClick={() => handleMenuAction(handleMultiJDSchedule, selectedActionCandidate)}>
                    <UserCheck size={15} className="text-emerald-600 shrink-0" />
                    <span className="font-medium">{t('candidate.scheduleInterview', 'Schedule Interview')}</span>
                </MenuItem>

                {/* 5. Comments */}
                <MenuItem
                    onClick={() => {
                        const cand = selectedActionCandidate;
                        handleCloseActionMenu();
                        setSelectedCandidateForComments(cand);
                        setShowCommentsModal(true);
                    }}
                >
                    <MessageSquare size={15} className="text-sky-600 shrink-0" />
                    <span className="font-medium">{t('candidate.comments', 'Comments')}</span>
                </MenuItem>

                {/* 6. Copy Invite Link */}
                {selectedActionCandidate?.inviteUrl && selectedActionCandidate?.inviteStatus === 'Active' && (
                    <MenuItem
                        onClick={() => {
                            const url = selectedActionCandidate.inviteUrl;
                            handleCloseActionMenu();
                            navigator.clipboard.writeText(url);
                            setSnackbar({ open: true, message: 'Invite link copied to clipboard!', severity: 'success' });
                        }}
                    >
                        <LinkIcon size={15} className="text-teal-600 shrink-0" />
                        <span className="font-medium">{t('candidate.copyInviteLink', 'Copy Invite Link')}</span>
                    </MenuItem>
                )}

                {/* Divider */}
                <div className="my-1 border-t border-slate-100" />

                {/* 7. Archive / Unarchive */}
                <MenuItem
                    onClick={() => {
                        const cand = selectedActionCandidate;
                        handleCloseActionMenu();
                        if (cand?.is_archived) {
                            handleUnarchiveCandidate(cand.id);
                        } else if (cand?.id) {
                            handleDeleteOpen(cand.id);
                        }
                    }}
                    sx={{
                        '&:hover': {
                            backgroundColor: selectedActionCandidate?.is_archived ? '#f0fdf4 !important' : '#fef2f2 !important',
                            color: selectedActionCandidate?.is_archived ? '#15803d !important' : '#b91c1c !important',
                        }
                    }}
                >
                    <IoArchiveOutline
                        size={15}
                        className={selectedActionCandidate?.is_archived ? "text-green-600 shrink-0" : "text-red-500 shrink-0"}
                    />
                    <span className={`font-medium ${selectedActionCandidate?.is_archived ? 'text-green-600' : 'text-red-600'}`}>
                        {selectedActionCandidate?.is_archived ? t('candidate.unarchive', 'Unarchive Candidate') : t('candidate.archive', 'Archive Candidate')}
                    </span>
                </MenuItem>
            </Menu>

            {/* JD Description Dialog (same as /jobdescriptions "View Description") */}
            <ShowDescriptionDialog
                open={jdDescriptionModal.open}
                onClose={() => setJdDescriptionModal({ open: false, title: '', content: null })}
                content={jdDescriptionModal.content}
                title={jdDescriptionModal.title}
            />

            {/* Timeline Drawer */}
            <Timeline
                open={timelineOpen}
                onClose={() => setTimelineOpen(false)}
                apiEndpoint="audit/timeline/candidates/"
                title={t('candidate.candidatesTimeline')}
                pageName={t('candidate.title')}
                width={450}
            />

            {/* Fixed Vertical Timeline Button - Center Right/Left based on RTL */}
            <button
                onClick={() => setTimelineOpen(true)}
                className={`fixed ${isRTL ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 z-40 flex items-center justify-center text-white p-2 rounded-full transition-all duration-200 group hover:scale-110`}
                style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)',
                    boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15)'
                }}
            >
                <Clock size={14} />
                <span className={`absolute ${isRTL ? 'left-full ml-3' : 'right-full mr-3'} px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none`}>
                    Timeline
                </span>
            </button>
        </PageShell>
    );
}